import {
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MicOffIcon from '@mui/icons-material/MicOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaletteIcon from '@mui/icons-material/Palette';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import TerminalIcon from '@mui/icons-material/Terminal';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { TABLE_THEME_ORDER, TABLE_THEMES, TableThemeId } from 'constants/tableThemes';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import GlobalContext from 'contexts/GlobalContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import { useFeatureFlag, useFeatureFlagExposure } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useOrientation } from 'hooks/useOrientation';
import { usePanelSlide } from 'hooks/usePanelSlide';
import { patchPlayerProfile, updateAvatar, updateDisplayName } from 'reducers/user';
import {
  BiometricBlocked,
  disableBiometric as disableNativeBiometric,
  enrollBiometric as enrollNativeBiometric,
  getBiometricBlockedReason,
  getBiometryLabel,
  isBiometricEnrolled,
  openIOSSettings
} from 'services/nativeBiometric';
import { setStreakFrameVisible } from 'services/streaksService';
import {
  clearAuth,
  clearPostLoginState,
  decodeJwtPayload,
  getAccessToken,
  getAuthToken
} from 'utils/auth';
import { clearBiometricCredentials } from 'utils/clearBiometricCredentials';
import { firePostRiskForDetailsChange } from 'utils/detailsChangeRisk';
import { SAFE_AREA_BOTTOM, safeAreaPanelInsets } from 'utils/safeArea';
import {
  APP_DOMAIN,
  BACKOFFICE_API_BASE,
  HELP_CENTER_URL,
  PRICING_URL,
  PROMOS_URL,
  TOURNAMENT_SCHEDULE_URL
} from 'utils/urls';

import {
  BiometryKind,
  biometryTitle,
  SensorGlyph
} from 'components/BiometricPrototype/biometricGlyphs';
import {
  BiometricSurfaceHost,
  BiometricUnavailableCard
} from 'components/BiometricPrototype/BiometricSurfaces';
import { DEBUG_CONSOLE_TOGGLE_EVENT } from 'components/Debug/debugConsolePreference';
import { isDebugConsoleEnabled, setDebugConsoleEnabled } from 'components/DebugConsole';
import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import { useOwnStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';
import StreakAvatarPicker, {
  AvatarPickerChange
} from 'components/Streaks/AvatarPicker/StreakAvatarPicker';
import { streakStore, useStreakSnapshot } from 'components/Streaks/state/streakStore';
import VersionLabel from 'components/VersionLabel';

import AvatarPickerModal from './AvatarPickerModal';
import ChangePasswordModal from './ChangePasswordModal';
import { usePlayerProfile } from './usePlayerProfile';

type ProfileTab = 'profile' | 'security' | 'verification' | 'settings' | 'more';
type SubView = 'tableSettings' | 'tableTheme';
// Off-screen position the panel slides in from (right-hand edge).
const PANEL_HIDDEN = { x: '100%', y: '0%' };
const ADDRESS_SUCCESS_CLEAR_MS = 2000;
const ADDRESS_ERROR_IDLE_CLEAR_MS = 5000;
const DISPLAY_NAME_SUCCESS_CLEAR_MS = 2000;
const DISPLAY_NAME_FAILURE_IDLE_CLEAR_MS = 5000;
type AddressFieldKey =
  | 'streetAddress'
  | 'postalAddress'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'countryCode';

const countryOptions = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'MX', label: 'Mexico' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'PH', label: 'Philippines' }
];

const getCountryLabel = (countryCode: string) => {
  const country = countryOptions.find((option) => option.code === countryCode);
  return country ? country.label : countryCode;
};

const addressFieldLabels: Record<AddressFieldKey, string> = {
  streetAddress: 'Home Address',
  postalAddress: 'Postal Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP',
  countryCode: 'Country'
};

const addressFieldKeys: AddressFieldKey[] = [
  'streetAddress',
  'postalAddress',
  'city',
  'state',
  'zipCode',
  'countryCode'
];

const requiredAddressFields: AddressFieldKey[] = [
  'streetAddress',
  'city',
  'state',
  'zipCode',
  'countryCode'
];

const addressFieldMaxLengths: Record<AddressFieldKey, number> = {
  streetAddress: 255,
  postalAddress: 255,
  city: 100,
  state: 100,
  zipCode: 20,
  countryCode: 10
};

type PhoneVerificationState = {
  phone: string;
  code: string;
  error: string;
  message: string;
  status: 'code' | 'success';
  verifying: boolean;
  resending: boolean;
  resendSeconds: number;
};

const openExternalUrl = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const isValidBitcoinAddress = (address: string) => {
  const sanitizedAddress = address.replace(/\s+/g, '');
  const legacyAddressPattern = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
  const segwitAddressPattern = /^bc1[ac-hj-np-z02-9]{25,90}$/i;

  return legacyAddressPattern.test(sanitizedAddress) || segwitAddressPattern.test(sanitizedAddress);
};

const normalizePhoneNumber = (phone: string) => {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');

  if (!digits) return '';
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11) return `+${digits}`;
  return trimmed;
};

const ProfilePanel = ({
  open,
  onClose,
  embedded = false
}: {
  open: boolean;
  onClose: () => void;
  // Embedded mode renders inside AccountMenuPanel: flat scrollable list (no tabs),
  // back-arrow header, and no logout (the outer menu owns it).
  embedded?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  // EN-11164: the streak frame around the player's own avatar, as at their seat.
  const streakFrame = useOwnStreakFrame();
  const systemLogAvailable = useFeatureFlag('systemLog');
  const streaksEnabled = useFeatureFlag('streaks');
  const streakSnapshot = useStreakSnapshot();
  const [debugConsoleOn, setDebugConsoleOn] = useState(() =>
    isDebugConsoleEnabled(user.playerGUID)
  );
  useEffect(() => {
    const syncDebugConsolePreference = () =>
      setDebugConsoleOn(isDebugConsoleEnabled(user.playerGUID));
    syncDebugConsolePreference();
    window.addEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, syncDebugConsolePreference);
    return () => window.removeEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, syncDebugConsolePreference);
  }, [user.playerGUID]);
  const { isMuted, setIsMuted } = useContext(GlobalContext);
  const tableSettings = useContext(TableSettingsContext);
  const animations = useAnimationPreferences();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [subView, setSubView] = useState<SubView | null>(null);
  useFeatureFlagExposure('reduceAnimations', 'settings', open && subView === 'tableSettings');
  // Theme picker stages a draft selection until the user taps "Save changes".
  const [draftTheme, setDraftTheme] = useState<TableThemeId>(tableSettings.theme);
  const [editing, setEditing] = useState<string | null>(null);
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [displayNameChecking, setDisplayNameChecking] = useState(false);
  const [displayNameMessage, setDisplayNameMessage] = useState('');
  const [displayNameMessageType, setDisplayNameMessageType] = useState<'success' | 'error'>(
    'success'
  );
  const [displayNameSuggestions, setDisplayNameSuggestions] = useState<string[]>([]);
  const displayNameCheckRef = useRef(0);
  const displayNameActivityRef = useRef(0);
  const displayNameFeedbackTimerRef = useRef<number | null>(null);
  const profileMessageTimerRef = useRef<number | null>(null);
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileMessageType, setProfileMessageType] = useState<'success' | 'error'>('success');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState('');
  const [avatarMessageType, setAvatarMessageType] = useState<'success' | 'error'>('success');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  // Native keeps its credential in the device keystore (services/nativeBiometric);
  // web keeps the WebAuthn passkey the auth app enrolled. Two stores, one toggle.
  const isNativeApp = Capacitor.isNativePlatform();
  const [passkeyEnrolled, setPasskeyEnrolled] = useState(() =>
    isNativeApp ? isBiometricEnrolled() : document.cookie.includes('hj_passkey=enrolled')
  );
  // Re-read on every open: enrollment can happen while this panel is mounted but
  // closed (the post-login BiometricEnrollmentPrompt enrolls from App level), and
  // a useState initializer only runs once — without this the toggle shows OFF
  // for a player who just enrolled seconds ago.
  useEffect(() => {
    if (!open) return;
    setPasskeyEnrolled(
      isNativeApp ? isBiometricEnrolled() : document.cookie.includes('hj_passkey=enrolled')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // AC11a/AC12 — the row renders for every password user; when the sensor is
  // unusable this says WHY, so it can dim and explain instead of hiding
  // (previously an `available &&` gate made the feature absent, not off, on
  // any device with nothing enrolled). Mirrors AccountMenuPanel exactly. AC10
  // is preserved by getBiometricBlockedReason's strong-biometry check: a
  // PIN-only device reports 'unsupported' rather than usable.
  const [biometricBlocked, setBiometricBlocked] = useState<BiometricBlocked | null>(null);
  const [biometricLabelKind, setBiometricLabelKind] = useState<BiometryKind>('faceid');
  const [showBiometricHelp, setShowBiometricHelp] = useState(false);
  useEffect(() => {
    if (!isNativeApp) {
      // Web's capability question is a platform authenticator actually
      // capable of user verification, not just the WebAuthn API existing —
      // PublicKeyCredential can exist with no usable biometric/PIN hardware
      // at all (roaming security-key support only). Mirrors AccountMenuPanel.
      let cancelled = false;
      if (typeof window.PublicKeyCredential === 'undefined') {
        setBiometricBlocked('unsupported');
        return;
      }
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then((available) => {
          if (!cancelled) setBiometricBlocked(available ? null : 'unsupported');
        })
        .catch(() => {
          if (!cancelled) setBiometricBlocked('unsupported');
        });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    void getBiometricBlockedReason().then((reason) => {
      if (!cancelled) setBiometricBlocked(reason);
    });
    void getBiometryLabel().then((label) => {
      if (cancelled) return;
      const l = (label || '').toLowerCase();
      setBiometricLabelKind(
        l.includes('face') ? 'faceid' : l.includes('touch') ? 'touchid' : 'fingerprint'
      );
    });
    return () => {
      cancelled = true;
    };
    // Re-checked on every open: the blocked reason changes outside the app
    // (the player enrolls a face in iOS Settings and comes back).
  }, [isNativeApp, open]);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState('');
  const [addressSaveSuccess, setAddressSaveSuccess] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [phoneVerification, setPhoneVerification] = useState<PhoneVerificationState | null>(null);
  const [phoneSaveStatus, setPhoneSaveStatus] = useState('');
  // Defer unmount until the close slide-out animation finishes — without this
  // the panel pops away when `open` goes false instead of sliding off-screen.
  const [rendered, setRendered] = useState(false);
  useEffect(() => {
    if (open) setRendered(true);
  }, [open]);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const addressActivityRef = useRef(0);
  const addressFeedbackTimerRef = useRef<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const savedTimerRef = useRef<number | null>(null);
  // Live refs used by the focus-profile-field event handler to avoid stale closures
  const editingRef = useRef<string | null>(null);
  const phoneVerificationRef = useRef<PhoneVerificationState | null>(null);
  const profilePhoneRef = useRef<string>('');

  const { profile, loading, fetchProfile, updateProfile, updateAddress } =
    usePlayerProfile('account-profile');

  // Keep live refs in sync on every render so the focus-profile-field event handler always
  // reads current values without needing stale-closure workarounds.
  editingRef.current = editing;
  phoneVerificationRef.current = phoneVerification;
  profilePhoneRef.current = profile.phone;

  // Local form state — initialized from API profile
  const [formData, setFormData] = useState({
    displayName: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    streetAddress: '',
    postalAddress: '',
    city: '',
    state: '',
    zipCode: '',
    countryCode: 'US',
    withdrawalWalletAddress: ''
  });

  // Derived verification statuses from API
  const idStatus = (profile.idVerified || 'PENDING') as
    | 'PENDING'
    | 'APPROVED'
    | 'REVIEWING'
    | 'DENIED'
    | 'SUSPECTED';
  const phoneVerified = profile.verifiedPhone;
  const emailVerified = profile.verifiedEmail;
  const clubValue =
    profile.clubName ||
    (profile.clubId ? 'Club details not available' : 'You are not part of a club yet');
  // ID Document value is the document TYPE (idDocType); verification status is
  // surfaced separately by the VERIFIED/UNVERIFIED badge. Empty → "Not set".
  const idDocumentValue = formatDocumentType(profile.idDocType) || 'Not set';
  const identityValue = getIdentityValue(profile.idReason, idStatus);
  const showDisplayNameFeedback = !editing || editing === 'displayName';

  function clearDisplayNameFeedbackTimer() {
    if (displayNameFeedbackTimerRef.current !== null) {
      window.clearTimeout(displayNameFeedbackTimerRef.current);
      displayNameFeedbackTimerRef.current = null;
    }
  }

  function clearDisplayNameFeedback() {
    clearDisplayNameFeedbackTimer();
    setDisplayNameMessage('');
    setDisplayNameSuggestions([]);
  }

  function markDisplayNameActivity() {
    displayNameActivityRef.current += 1;
    clearDisplayNameFeedbackTimer();
  }

  function scheduleDisplayNameFeedbackClear(delayMs: number, activityToken?: number) {
    clearDisplayNameFeedbackTimer();
    displayNameFeedbackTimerRef.current = window.setTimeout(() => {
      if (activityToken === undefined || displayNameActivityRef.current === activityToken) {
        clearDisplayNameFeedback();
      }
    }, delayMs);
  }

  function keepDisplayNameFailureVisibleWhileActive() {
    if (
      displayNameMessageType === 'error' &&
      (displayNameMessage || displayNameSuggestions.length > 0)
    ) {
      markDisplayNameActivity();
      scheduleDisplayNameFeedbackClear(
        DISPLAY_NAME_FAILURE_IDLE_CLEAR_MS,
        displayNameActivityRef.current
      );
    }
  }

  // Fetch profile on open
  useEffect(() => {
    if (open) {
      fetchProfile();
    }
  }, [open, fetchProfile]);

  function clearAddressFeedbackTimer() {
    if (addressFeedbackTimerRef.current !== null) {
      window.clearTimeout(addressFeedbackTimerRef.current);
      addressFeedbackTimerRef.current = null;
    }
  }

  function clearAddressFeedback() {
    clearAddressFeedbackTimer();
    setAddressSaveError('');
    setAddressSaveSuccess('');
  }

  function scheduleAddressFeedbackClear(delayMs: number, activityToken?: number) {
    clearAddressFeedbackTimer();
    addressFeedbackTimerRef.current = window.setTimeout(() => {
      if (activityToken === undefined || addressActivityRef.current === activityToken) {
        clearAddressFeedback();
      }
    }, delayMs);
  }

  function markAddressActivity() {
    addressActivityRef.current += 1;
    clearAddressFeedbackTimer();
    if (addressSaveError) {
      scheduleAddressFeedbackClear(ADDRESS_ERROR_IDLE_CLEAR_MS, addressActivityRef.current);
    } else if (addressSaveSuccess) {
      clearAddressFeedback();
    }
  }

  function updateAddressField(field: AddressFieldKey, value: string) {
    markAddressActivity();
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  useEffect(() => () => clearAddressFeedbackTimer(), []);

  // Sync form data when profile loads
  useEffect(() => {
    const hasProfileData = Boolean(
      profile.email ||
        user.playerEmail ||
        profile.displayName ||
        profile.firstName ||
        profile.lastName ||
        profile.phone ||
        profile.streetAddress ||
        profile.postalAddress ||
        profile.city ||
        profile.state ||
        profile.zipCode
    );

    if (!loading && hasProfileData) {
      setFormData({
        displayName: profile.displayName || user.displayName || user.playerName || '',
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        email: profile.email || user.playerEmail || '',
        streetAddress: profile.streetAddress,
        postalAddress: profile.postalAddress,
        city: profile.city,
        state: profile.state,
        zipCode: profile.zipCode,
        countryCode: profile.countryCode || 'US',
        withdrawalWalletAddress: profile.withdrawalWalletAddress || ''
      });
    }
  }, [profile, loading, user.displayName, user.playerEmail, user.playerName]);

  // Reset sub-navigation each time the panel is reopened so the user lands on
  // the main profile view rather than a leftover sub-screen.
  useEffect(() => {
    if (open) {
      setSubView(null);
      setDraftTheme(tableSettings.theme);
      clearDisplayNameFeedback();
    }
  }, [open, tableSettings.theme]);

  useEffect(() => () => clearDisplayNameFeedbackTimer(), []);

  useEffect(() => {
    if (
      !phoneVerification ||
      phoneVerification.status !== 'code' ||
      phoneVerification.resendSeconds <= 0
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setPhoneVerification((prev) => {
        if (!prev || prev.status !== 'code' || prev.resendSeconds <= 0) return prev;
        return { ...prev, resendSeconds: prev.resendSeconds - 1 };
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phoneVerification?.status, phoneVerification?.resendSeconds]);

  // Auto-dismiss the top profile message after 3 s; also cleared on any user action.
  const showProfileMessage = useCallback((msg: string, type: 'success' | 'error') => {
    if (profileMessageTimerRef.current) window.clearTimeout(profileMessageTimerRef.current);
    setProfileMessageType(type);
    setProfileMessage(msg);
    profileMessageTimerRef.current = window.setTimeout(() => setProfileMessage(''), 3000);
  }, []);

  // Dismiss immediately on any edit / navigation action.
  const clearProfileMessage = useCallback(() => {
    if (profileMessageTimerRef.current) window.clearTimeout(profileMessageTimerRef.current);
    setProfileMessage('');
  }, []);

  useEffect(() => {
    const handleFocusProfileField = (event: Event) => {
      const customEvent = event as CustomEvent<{ field?: string }>;
      const field = customEvent.detail?.field;
      if (!field) return;
      setActiveTab('profile');
      setSubView(null);
      if (field !== 'phone') {
        revertPhoneToSaved(
          editingRef.current,
          phoneVerificationRef.current,
          profilePhoneRef.current
        );
      }
      setEditing(field);
      setHighlightedField(field);

      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
      window.setTimeout(() => {
        document.querySelector(`[data-profile-field="${field}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 0);
      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightedField(null);
        highlightTimerRef.current = null;
      }, 1800);
    };

    window.addEventListener('focus-profile-field', handleFocusProfileField);
    return () => {
      window.removeEventListener('focus-profile-field', handleFocusProfileField);
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Slide animation — always horizontal (slide from right), on both desktop
  // and mobile. Defers unmount via `rendered` so the panel animates off-screen
  // instead of popping away when `open` goes false.
  usePanelSlide({
    open,
    rendered,
    panelRef,
    backdropRef,
    hidden: PANEL_HIDDEN,
    onClosed: () => setRendered(false)
  });

  // Reverts the phone input to the last saved value and clears any in-progress OTP state.
  // Call this whenever any field other than phone starts editing.
  const revertPhoneToSaved = (
    currentEditing: string | null,
    currentPhoneVerification: PhoneVerificationState | null,
    savedPhone: string
  ) => {
    if (currentEditing !== 'phone' && !currentPhoneVerification) return;
    setFormData((prev) => ({ ...prev, phone: savedPhone }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });
    if (currentPhoneVerification) {
      setPhoneVerification(null);
    }
  };

  const handleEdit = (field: string) => {
    clearProfileMessage();
    if (field !== 'displayName') {
      clearDisplayNameFeedback();
    } else {
      keepDisplayNameFailureVisibleWhileActive();
    }
    if (field !== 'phone') {
      revertPhoneToSaved(editing, phoneVerification, profile.phone);
    }
    // Switching directly from one field's edit to another discards the first
    // field's unsaved draft (phone has its own revert above).
    discardPreviousEdit(editing, field);
    setEditing(field);
  };

  // The saved (source-of-truth) value for a field, mirroring the formData sync
  // effect. Used by Cancel to discard unsaved edits.
  const savedFieldValue = (field: string): string => {
    switch (field) {
      case 'displayName':
        return profile.displayName || user.displayName || user.playerName || '';
      case 'email':
        return profile.email || user.playerEmail || '';
      case 'countryCode':
        return profile.countryCode || 'US';
      case 'withdrawalWalletAddress':
        return profile.withdrawalWalletAddress || '';
      default:
        return (profile as any)[field] ?? '';
    }
  };

  // Restore a field to its saved value and clear its validation error —
  // discards an unsaved inline-edit draft.
  const restoreField = (field: string) => {
    setFormData((prev) => ({ ...prev, [field]: savedFieldValue(field) }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Cancel an inline edit: restore the field so the typed (or cleared) draft
  // doesn't stick — fixes the wallet address falling back to its "Not set"
  // default on Cancel (EN-9838 QA).
  const handleCancel = (field: string) => {
    restoreField(field);
    setEditing(null);
  };

  // Returns whether the account-level flag was actually confirmed updated.
  // Callers must not treat the toggle as settled on `false` — the device
  // credential and the account flag disagreeing is what leaves a player with
  // a biometric login the next attempt then fails or a setting that silently
  // didn't take.
  const updateSecureLoginFlag = async (enabled: boolean): Promise<boolean> => {
    const { access_token } = getAuthToken();
    let userId = user.playerGUID;
    if (!userId && access_token) {
      try {
        // decodeJwtPayload, not a raw atob() — a real Descope token is
        // base64url and its `-`/`_` characters make plain atob() throw,
        // silently swallowed by this catch and leaving userId unset on
        // every real token (EN-5415).
        const payload = decodeJwtPayload(access_token);
        userId = payload.sub || '';
      } catch (_e) {
        /* ignore */
      }
    }
    if (!userId || !access_token) return false;
    try {
      const response = await fetch(`${BACKOFFICE_API_BASE}/descope/updateSecureLogin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          Authorization: `Bearer ${access_token}`
        },
        body: new URLSearchParams({
          userId,
          enableSecureLogin: enabled ? '1' : '0'
        }).toString()
      });
      const body = await response.json().catch(() => null);
      if (!response.ok || body?.success !== true) return false;
    } catch (err) {
      console.error('[Biometrics] Failed to sync enableSecureLogin:', err);
      return false;
    }
    dispatch(patchPlayerProfile({ enableSecureLogin: enabled }));
    try {
      localStorage.setItem('hj_biometric_enabled', String(enabled));
    } catch (_e) {
      /* ignore */
    }
    return true;
  };

  const handleBiometricToggle = async () => {
    if (isNativeApp) {
      if (passkeyEnrolled) {
        // Deliberately cleared BEFORE the account-level sync — see
        // AccountMenuPanel.tsx's handleBiometricToggle disable branch for the
        // full reasoning (mirrored here): `enableSecureLogin` never gates
        // login (password/social sign-in is always available regardless of
        // its value), so a failed sync cannot lock the account out. Clearing
        // local state first instead means a failed sync only leaves this
        // device's credential missing a moment longer than the server
        // record — not a live, still-unlockable credential outliving the
        // player's explicit disable, which is what the reverse order risks.
        const deleted = await disableNativeBiometric();
        setPasskeyEnrolled(false);
        const synced = await updateSecureLoginFlag(false);
        setProfileMessageType(!deleted.ok || !synced ? 'error' : 'success');
        setProfileMessage(
          !deleted.ok
            ? 'Biometric login is off on this device, but a stored credential could not be fully cleared. If this persists, reinstalling the app will remove it.'
            : synced
            ? 'Biometric login disabled.'
            : 'Biometric login was disabled on this device, but syncing that change to your account failed. Please try again.'
        );
        return;
      }
      const email = user.playerEmail;
      const { refresh_token: refreshToken } = getAuthToken();
      if (!email || !refreshToken) {
        setProfileMessageType('error');
        setProfileMessage('Could not enable biometric login. Please try again.');
        return;
      }
      setIsEnrolling(true);
      try {
        const result = await enrollNativeBiometric(email, refreshToken);
        if (!result.ok) {
          setPasskeyEnrolled(false);
          setProfileMessageType('error');
          setProfileMessage(
            `Biometric setup failed: ${result.error || 'cancelled or unavailable'}`
          );
          return;
        }
        const synced = await updateSecureLoginFlag(true);
        if (!synced) {
          // Don't leave a device credential the account-level flag disagrees
          // with — a later login could otherwise honor a credential the
          // account itself never actually opted into.
          await disableNativeBiometric();
          setPasskeyEnrolled(false);
          setProfileMessageType('error');
          setProfileMessage('Could not enable biometric login. Please try again.');
          return;
        }
        setPasskeyEnrolled(true);
        setProfileMessageType('success');
        setProfileMessage('Biometric login enabled on this device!');
      } finally {
        setIsEnrolling(false);
      }
      return;
    }

    const parts = window.location.hostname.split('.');
    const domain = parts.length >= 2 ? parts.slice(-2).join('.') : '';
    const domainAttr = domain ? `domain=.${domain}; ` : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    if (passkeyEnrolled) {
      // Shared with AccountMenuPanel.tsx's disable branch (and logout/reset)
      // so every caller clears the exact same set of keys — a hand-rolled
      // copy here previously matched AccountMenuPanel.tsx's cookie/localStorage
      // set field-for-field, but any future addition to one risked silently
      // drifting from the other.
      await clearBiometricCredentials(user.playerEmail);
      setPasskeyEnrolled(false);
      const synced = await updateSecureLoginFlag(false);
      setProfileMessageType(synced ? 'success' : 'error');
      setProfileMessage(
        synced
          ? 'Biometric login disabled for this device.'
          : 'Biometric login was disabled on this device, but syncing that change to your account failed. Please try again.'
      );
      return;
    }

    const email = user.playerEmail;
    const { refresh_token: refreshToken } = getAuthToken();
    if (!email || !refreshToken) {
      setProfileMessage('Could not start enrollment. Please try again.');
      setProfileMessageType('error');
      return;
    }

    setIsEnrolling(true);
    try {
      // Store the refresh token so the auth app can use it for biometric sign-in.
      // Uses the same localStorage key as apps/auth/src/utils/passkey.js.
      try {
        localStorage.setItem(`hj_biometric_rt_${email}`, refreshToken);
      } catch {
        /* ignore */
      }

      // Create a local WebAuthn platform credential (Face ID / fingerprint gate).
      // Same logic as createBiometricCredential() in passkey.js — the rpId must
      // be the parent domain so the credential is usable from the auth app too.
      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      const rpId =
        !hostname || hostname === 'localhost'
          ? 'localhost'
          : hostParts.length > 2
          ? hostParts.slice(-2).join('.')
          : hostname;

      let credId: string | null = null;
      try {
        const cred = await navigator.credentials.create({
          publicKey: {
            challenge: crypto.getRandomValues(new Uint8Array(32)).buffer,
            rp: { id: rpId, name: 'Hijack Poker' },
            user: {
              id: crypto.getRandomValues(new Uint8Array(16)).buffer,
              name: email,
              displayName: email
            },
            pubKeyCredParams: [
              { type: 'public-key', alg: -7 },
              { type: 'public-key', alg: -257 }
            ],
            authenticatorSelection: {
              authenticatorAttachment: 'platform',
              // 'required', not 'preferred' (matches AccountMenuPanel.tsx's
              // copy of this same ceremony): this credential is labeled and
              // trusted as "biometric login" everywhere downstream — a
              // ceremony that merely prefers verification can succeed with
              // none at all, which would enroll a device with no proof a
              // fingerprint/face/PIN check ever happened.
              userVerification: 'required'
            },
            timeout: 60000
          }
        } as CredentialCreationOptions);
        if (cred) {
          const rawId = (cred as PublicKeyCredential).rawId;
          credId = btoa(Array.from(new Uint8Array(rawId), (b) => String.fromCharCode(b)).join(''))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
        }
      } catch (err: any) {
        console.error('[Biometrics] credential create error:', err?.name, err?.message);
        if (err?.name === 'NotAllowedError') {
          try {
            localStorage.removeItem(`hj_biometric_rt_${email}`);
          } catch {
            /* ignore */
          }
          return;
        }
        if (err?.name === 'SecurityError') {
          setProfileMessage('Biometric setup requires a secure (HTTPS) connection.');
          setProfileMessageType('error');
          return;
        }
        if (err?.name === 'NotSupportedError') {
          setProfileMessage('This device or browser does not support biometric login.');
          setProfileMessageType('error');
          return;
        }
        throw err;
      }

      if (!credId) {
        try {
          localStorage.removeItem(`hj_biometric_rt_${email}`);
        } catch {
          /* ignore */
        }
        setProfileMessage('Biometric setup is not supported on this device.');
        setProfileMessageType('error');
        return;
      }

      try {
        localStorage.setItem(`hj_biometric_cred_${email}`, credId);
      } catch {
        /* ignore */
      }

      const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
      // Write RT and credId to parent-domain cookies so the auth app (different
      // subdomain, separate localStorage) can read them on the login screen.
      const { refresh_token: refreshTokenForCookie } = getAuthToken();
      if (refreshTokenForCookie) {
        document.cookie = `hj_biometric_rt=${encodeURIComponent(
          refreshTokenForCookie
        )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      }
      document.cookie = `hj_biometric_cred=${encodeURIComponent(
        credId
      )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      document.cookie = `hj_passkey=enrolled; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      document.cookie = `hj_passkey_email=${encodeURIComponent(
        email
      )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;

      const synced = await updateSecureLoginFlag(true);
      if (!synced) {
        // Don't leave a device credential the account-level flag disagrees
        // with — a later login could otherwise honor a credential the
        // account itself never actually opted into.
        await clearBiometricCredentials(email);
        setPasskeyEnrolled(false);
        setProfileMessageType('error');
        setProfileMessage('Could not enable biometric login. Please try again.');
        return;
      }

      setPasskeyEnrolled(true);
      setProfileMessageType('success');
      setProfileMessage('Biometric login enabled on this device!');
    } catch (err: any) {
      console.error('[Biometrics] setup failed:', err?.name, err?.message);
      try {
        localStorage.removeItem(`hj_biometric_rt_${email}`);
      } catch {
        /* ignore */
      }
      setProfileMessage(
        `Biometric setup failed: ${err?.name || 'unknown error'}. Please try again.`
      );
      setProfileMessageType('error');
    } finally {
      setIsEnrolling(false);
    }
  };

  // Cancel an in-progress ADDRESS edit. The address block edits several
  // subfields at once, so restore every one to its saved value (not just the
  // active field) and clear any address feedback — otherwise the typed-but-
  // discarded values stick in the inputs (EN-9XXX QA).
  const handleCancelAddress = () => {
    addressFieldKeys.forEach((field) => restoreField(field));
    clearAddressFeedback();
    setEditing(null);
  };

  // Switching directly from one field's edit to another (clicking EDIT on a
  // different field instead of Cancel) must discard the first field's unsaved
  // draft. Address subfields restore the WHOLE group — but switching BETWEEN two
  // address subfields keeps the draft (you're still editing the same address
  // block). Phone is reverted separately via revertPhoneToSaved.
  const discardPreviousEdit = (prevField: string | null, nextField: string) => {
    if (!prevField || prevField === nextField || prevField === 'phone') return;
    const prevIsAddress = addressFieldKeys.includes(prevField as AddressFieldKey);
    const nextIsAddress = addressFieldKeys.includes(nextField as AddressFieldKey);
    if (prevIsAddress && nextIsAddress) return;
    if (prevIsAddress) {
      addressFieldKeys.forEach((field) => restoreField(field));
      clearAddressFeedback();
    } else {
      restoreField(prevField);
    }
  };

  const validateDisplayName = async (displayName: string) => {
    const params = new URLSearchParams({
      displayName,
      email: formData.email || user.playerEmail || '',
      playerGUID: user.playerGUID || '',
      dateChanged: profile.displayNameDateChanged || ''
    });

    const response = await fetch(
      `${BACKOFFICE_API_BASE}/descope/checkDisplayName?${params.toString()}`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
    const json = await response.json().catch(() => null);
    const suggestions = Object.values(json?.suggestions || {}).filter(
      (suggestion): suggestion is string => typeof suggestion === 'string'
    );

    if (!json || json.result !== 'valid') {
      return {
        valid: false,
        message: json?.message || 'An unexpected error occurred while checking the display name.',
        suggestions
      };
    }

    return {
      valid: true,
      message: json.message || 'Display name is available.',
      suggestions
    };
  };

  const checkPhoneAvailability = async (phone: string) => {
    try {
      const response = await fetch(
        `${BACKOFFICE_API_BASE}/descope/checkPhone?phonenumber=${encodeURIComponent(phone)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${getAccessToken()}`
          }
        }
      );
      const json = await response.json().catch(() => null);

      if (!response.ok || json?.result !== 'valid') {
        return {
          valid: false,
          message: json?.message || 'The phone number is already in use by another user.'
        };
      }

      return { valid: true, message: '' };
    } catch {
      return {
        valid: false,
        message: 'Unable to check phone number. Please try again.'
      };
    }
  };

  const enrollPhoneForVerification = async (phone: string) => {
    const { access_token, refresh_token } = getAuthToken();
    const loginId = profile.email || user.playerEmail;

    if (!loginId || !refresh_token) {
      throw new Error('Unable to start phone verification. Please sign in again.');
    }

    const response = await fetch(`${BACKOFFICE_API_BASE}/descope/enrollPhone`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        Authorization: `Bearer ${access_token}`
      },
      body: new URLSearchParams({
        loginId,
        phone_number: phone,
        token: refresh_token
      }).toString()
    });
    const json = await response.json().catch(() => null);
    const status = json?.original?.status || json?.status;

    if (!response.ok || status !== 'PENDING_ACTIVATION') {
      const errorCode = json?.original?.errorCode || json?.errorCode || '';
      if (errorCode === 'E032106') {
        throw new Error('Enter a valid phone number before saving.');
      }
      throw new Error('Unable to send verification code. Please try again.');
    }
  };

  const startPhoneVerification = async (phone: string) => {
    if (!phone.trim()) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: 'Phone number cannot be empty.'
      }));
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phone);

    if (!normalizedPhone || normalizedPhone.replace(/\D/g, '').length < 11) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: 'Enter a valid phone number before saving.'
      }));
      return;
    }

    if (normalizedPhone === normalizePhoneNumber(profile.phone || '')) {
      setEditing(null);
      setFormData((prev) => ({ ...prev, phone: profile.phone }));
      return;
    }

    setSavingField('phone');
    setSavedField(null);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });

    try {
      setPhoneSaveStatus('Checking phone number...');
      const availability = await checkPhoneAvailability(normalizedPhone);

      if (!availability.valid) {
        setFieldErrors((prev) => ({
          ...prev,
          phone: availability.message
        }));
        return;
      }

      setPhoneSaveStatus('Sending verification code...');
      await enrollPhoneForVerification(normalizedPhone);
      setEditing(null);
      setFormData((prev) => ({ ...prev, phone: profile.phone }));
      setPhoneVerification({
        phone: normalizedPhone,
        code: '',
        error: '',
        message: 'We sent a verification code.',
        status: 'code',
        verifying: false,
        resending: false,
        resendSeconds: 120
      });
    } catch (error: any) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: error?.message || 'Unable to send verification code.'
      }));
    } finally {
      setPhoneSaveStatus('');
      setSavingField(null);
    }
  };

  const verifyPhoneCode = async () => {
    if (!phoneVerification || phoneVerification.verifying) return;
    const code = phoneVerification.code.replace(/\D/g, '');

    if (code.length !== 6) {
      setPhoneVerification((prev) =>
        prev ? { ...prev, error: 'Please enter the 6-digit code.' } : prev
      );
      return;
    }

    setPhoneVerification((prev) => (prev ? { ...prev, error: '', verifying: true } : prev));

    try {
      const loginId = profile.email || user.playerEmail;
      const response = await fetch(`${BACKOFFICE_API_BASE}/descope/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({
          loginId,
          type: 'sms',
          code
        })
      });
      const json = await response.json().catch(() => null);
      const status = json?.original?.status || json?.status;
      const active = json?.original?.active ?? json?.active;

      if (!response.ok || status !== 'ACTIVE' || active !== true) {
        setPhoneVerification((prev) =>
          prev
            ? {
                ...prev,
                code: '',
                error: 'Invalid code. Please try again.',
                verifying: false
              }
            : prev
        );
        return;
      }

      // Phone is already updated in Descope via OTP verification; sync local state only
      setFormData((prev) => ({ ...prev, phone: phoneVerification.phone }));
      setSavedField('phone');
      setPhoneVerification((prev) =>
        prev ? { ...prev, status: 'success', verifying: false } : prev
      );
      await fetchProfile({ silent: true });
      // Mirror Unity AddPhoneView.cs:154 — fire-and-forget PostRisk after a
      // successful phone add/verify.
      firePostRiskForDetailsChange(user.playerGUID || '');
      window.setTimeout(() => setPhoneVerification(null), 2000);
    } catch (error) {
      setPhoneVerification((prev) =>
        prev
          ? {
              ...prev,
              code: '',
              error: 'Verification failed. Please try again.',
              verifying: false
            }
          : prev
      );
    }
  };

  const resendPhoneCode = async () => {
    if (
      !phoneVerification ||
      phoneVerification.status !== 'code' ||
      phoneVerification.resending ||
      phoneVerification.resendSeconds > 0
    ) {
      return;
    }

    setPhoneVerification((prev) =>
      prev ? { ...prev, error: '', message: '', resending: true } : prev
    );

    try {
      await enrollPhoneForVerification(phoneVerification.phone);
      setPhoneVerification((prev) =>
        prev
          ? {
              ...prev,
              code: '',
              error: '',
              message: 'We sent a new 6-digit code.',
              resending: false,
              resendSeconds: 120
            }
          : prev
      );
    } catch (error: any) {
      setPhoneVerification((prev) =>
        prev
          ? {
              ...prev,
              error: error?.message || 'Unable to resend verification code.',
              message: '',
              resending: false
            }
          : prev
      );
    }
  };

  const handleSave = async (field: string) => {
    if (savingField) return;

    const rawValue = (formData as any)[field];
    const value =
      field === 'withdrawalWalletAddress'
        ? String(rawValue || '').replace(/\s+/g, '')
        : typeof rawValue === 'string'
        ? rawValue.trim()
        : rawValue;

    clearProfileMessage();
    if (field === 'withdrawalWalletAddress') {
      // (message already cleared above)
    }

    if (field === 'withdrawalWalletAddress' && value && !isValidBitcoinAddress(value)) {
      setSavedField(null);
      setFieldErrors((prev) => ({
        ...prev,
        [field]: 'Enter a valid BTC withdrawal address before saving.'
      }));
      return;
    }

    if (field === 'phone') {
      await startPhoneVerification(value);
      return;
    }

    setSavingField(field);
    setSavedField(null);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

    try {
      if (field === 'displayName') {
        markDisplayNameActivity();
        displayNameCheckRef.current += 1;
        setDisplayNameChecking(false);
        setDisplayNameMessage('');
        const validationResult = await validateDisplayName(value);
        setDisplayNameMessageType(validationResult.valid ? 'success' : 'error');
        setDisplayNameMessage(validationResult.message);
        setDisplayNameSuggestions(validationResult.suggestions);
        if (!validationResult.valid) {
          setDisplayNameMessageType('error');
          setFormData((prev) => ({
            ...prev,
            displayName: profile.displayName || user.displayName || user.playerName || ''
          }));
          setEditing(null);
          await fetchProfile({ silent: true });
          scheduleDisplayNameFeedbackClear(
            DISPLAY_NAME_FAILURE_IDLE_CLEAR_MS,
            displayNameActivityRef.current
          );
          return;
        }
      }

      const result = await updateProfile({ [field]: value });
      if (result.success) {
        setSavedField(field);

        if (field === 'displayName') {
          dispatch(updateDisplayName(value));
          setDisplayNameMessageType('success');
          setDisplayNameMessage('Display name successfully saved.');
          setDisplayNameSuggestions([]);
          scheduleDisplayNameFeedbackClear(DISPLAY_NAME_SUCCESS_CLEAR_MS);
        } else if (field !== 'withdrawalWalletAddress') {
          showProfileMessage(result.message || 'Profile updated successfully.', 'success');
        }
        setFormData((prev) => ({ ...prev, [field]: value }));
        setEditing(null);

        if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = window.setTimeout(() => {
          setSavedField(null);
          savedTimerRef.current = null;
        }, 2200);

        if (field === 'withdrawalWalletAddress') {
          setHighlightedField(field);
          if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
          highlightTimerRef.current = window.setTimeout(() => {
            setHighlightedField(null);
            highlightTimerRef.current = null;
          }, 1200);
        }

        await fetchProfile({ silent: true });
      } else {
        setSavedField(null);
        setFieldErrors((prev) => ({
          ...prev,
          [field]: result.message || 'Update failed.'
        }));
        if (field === 'displayName') {
          setDisplayNameMessageType('error');
          setDisplayNameMessage(result.message || 'Unable to update display name.');
          setDisplayNameSuggestions([]);
          scheduleDisplayNameFeedbackClear(
            DISPLAY_NAME_FAILURE_IDLE_CLEAR_MS,
            displayNameActivityRef.current
          );
        } else if (field !== 'withdrawalWalletAddress') {
          showProfileMessage(result.message || 'Update failed.', 'error');
        }
        setFormData((prev) => ({
          ...prev,
          [field]: (profile as any)[field] || prev[field as keyof typeof prev]
        }));
        setEditing(null);
        await fetchProfile({ silent: true });
      }
    } catch (error) {
      if (field === 'displayName') {
        setDisplayNameMessageType('error');
        setDisplayNameMessage('Unable to update display name.');
        setDisplayNameSuggestions([]);
        scheduleDisplayNameFeedbackClear(
          DISPLAY_NAME_FAILURE_IDLE_CLEAR_MS,
          displayNameActivityRef.current
        );
      } else if (field === 'withdrawalWalletAddress') {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: 'Update failed.'
        }));
      } else {
        showProfileMessage('Update failed.', 'error');
      }
    } finally {
      setSavingField(null);
    }
  };

  const handleAvatarConfirm = async (avatar: string) => {
    if (!avatar || avatar === user.playerAvatar) {
      return;
    }

    setAvatarSaving(true);
    setAvatarMessage('');

    const result = await updateProfile({ avatar });
    setAvatarSaving(false);
    setAvatarMessageType(result.success ? 'success' : 'error');
    setAvatarMessage(
      result.success ? 'Avatar successfully saved.' : result.message || 'Avatar change failed.'
    );

    if (result.success) {
      dispatch(updateAvatar({ playerAvatar: avatar }));
    }
  };

  // Change Avatar with the streak ladder (EN-11164): the frame switch first,
  // then the avatar, so a failed avatar save leaves only the avatar to retry.
  const saveStreakAvatarChange = async ({ avatar, showFrame }: AvatarPickerChange) => {
    if (showFrame !== undefined) {
      try {
        streakStore.accept(await setStreakFrameVisible(showFrame));
      } catch (e) {
        return (e as Error)?.message || 'Could not save the frame setting. Try again.';
      }
    }
    if (avatar) {
      const result = await updateProfile({ avatar });
      if (!result.success) return result.message || 'Avatar change failed.';
      dispatch(updateAvatar({ playerAvatar: avatar }));
    }
    return null;
  };

  const editAddressField = (field: AddressFieldKey) => {
    markAddressActivity();
    revertPhoneToSaved(editing, phoneVerification, profile.phone);
    // Entering the address editor from another field discards that field's
    // unsaved draft (switching between address subfields keeps the draft).
    discardPreviousEdit(editing, field);
    setEditing(field);
  };

  const handleAddressSave = async (activeAddressField: AddressFieldKey) => {
    clearAddressFeedback();
    const getAddressValue = (field: AddressFieldKey) =>
      field === activeAddressField ? formData[field] : formData[field] || profile[field];

    const addressFields = {
      streetAddress: getAddressValue('streetAddress').trim(),
      postalAddress: getAddressValue('postalAddress').trim(),
      city: getAddressValue('city').trim(),
      state: getAddressValue('state').trim(),
      zipCode: getAddressValue('zipCode').trim(),
      countryCode: (getAddressValue('countryCode') || 'US').trim()
    };

    const validationError = getAddressValidationError(addressFields);
    if (validationError) {
      setAddressSaveError(validationError);
      scheduleAddressFeedbackClear(ADDRESS_ERROR_IDLE_CLEAR_MS, addressActivityRef.current);
      return;
    }

    setAddressSaving(true);

    const result = await updateAddress(addressFields);

    setAddressSaving(false);

    if (result === true) {
      setAddressSaveSuccess(`${addressFieldLabels[activeAddressField]} updated successfully.`);
      setEditing(null);
      scheduleAddressFeedbackClear(ADDRESS_SUCCESS_CLEAR_MS);
      return;
    }

    setAddressSaveError(
      typeof result === 'string'
        ? result
        : 'We could not save your address. Please check the details and try again.'
    );
    scheduleAddressFeedbackClear(ADDRESS_ERROR_IDLE_CLEAR_MS, addressActivityRef.current);
  };

  const tabs: { key: ProfileTab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile', label: 'Profile', icon: <PersonIcon style={{ fontSize: '0.85rem' }} /> },
    { key: 'security', label: 'Security', icon: <SecurityIcon style={{ fontSize: '0.85rem' }} /> },
    {
      key: 'verification',
      label: 'Verify',
      icon: <VerifiedUserIcon style={{ fontSize: '0.85rem' }} />
    },
    { key: 'settings', label: 'Settings', icon: <SettingsIcon style={{ fontSize: '0.85rem' }} /> },
    { key: 'more', label: 'More', icon: <MoreVertIcon style={{ fontSize: '0.85rem' }} /> }
  ];

  if (!rendered) return null;

  /* ─── Table Settings sub-view ─── */
  const tableSettingsView = (
    <>
      <div style={subHeaderStyle}>
        <button onClick={() => setSubView(null)} style={subBackBtn}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Table Settings</span>
        <button onClick={onClose} style={closeBtn}>
          <CloseIcon style={{ fontSize: '1.2rem' }} />
        </button>
      </div>
      <div style={contentStyle}>
        <div style={sectionCol}>
          <button style={actionCard} onClick={() => setSubView('tableTheme')}>
            <PaletteIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
            <div style={actionCardInfo}>
              <span style={actionCardLabel}>Change table theme</span>
              <span style={actionCardDesc}>{TABLE_THEMES[tableSettings.theme].name}</span>
            </div>
            <ChevronRightIcon style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
          </button>

          <ToggleRow
            icon={
              <ChatBubbleOutlineIcon
                style={{
                  fontSize: '1.1rem',
                  color: tableSettings.chatEnabled ? '#4CAF50' : 'rgba(255,255,255,0.3)'
                }}
              />
            }
            label='Table chat enabled'
            checked={tableSettings.chatEnabled}
            onChange={tableSettings.setChatEnabled}
          />

          <ToggleRow
            icon={
              isMuted ? (
                <VolumeOffIcon style={{ fontSize: '1.1rem', color: '#c0392b' }} />
              ) : (
                <VolumeUpIcon style={{ fontSize: '1.1rem', color: '#4CAF50' }} />
              )
            }
            label='Table sounds disabled'
            checked={isMuted}
            onChange={setIsMuted}
          />

          {/* Hidden while ALL table sounds are off — it would have no effect, and
              Unity hides it the same way (MenuForTablesView.cs:120/242/493). The
              saved value is untouched, so re-enabling sounds brings the row back
              exactly as the player left it. This ToggleRow has no description
              slot (neither do the two rows above it), so only the label changes. */}
          {!isMuted && (
            <ToggleRow
              icon={
                <MicOffIcon
                  style={{
                    fontSize: '1.1rem',
                    color: tableSettings.muteOtherPlayers ? '#c0392b' : 'rgba(255,255,255,0.3)'
                  }}
                />
              }
              label="Mute Other Players' Sounds"
              checked={tableSettings.muteOtherPlayers}
              onChange={tableSettings.setMuteOtherPlayers}
            />
          )}
          {animations.available && (
            <>
              <ToggleRow
                icon={<SettingsIcon style={{ fontSize: '1.1rem' }} />}
                label='Reduce animations'
                checked={animations.ready && animations.reduceAnimations}
                disabled={!animations.ready || animations.busy}
                onChange={animations.setReduceAnimations}
              />
              {animations.busy && <span role='status'>Updating animation settings…</span>}
              {animations.error && (
                <div role='alert'>
                  {animations.error} <button onClick={animations.retry}>Try again</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  /* ─── Table Theme picker sub-view ─── */
  const tableThemeView = (
    <>
      <div style={subHeaderStyle}>
        <button onClick={() => setSubView('tableSettings')} style={subBackBtn}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Table Theme</span>
        <button onClick={onClose} style={closeBtn}>
          <CloseIcon style={{ fontSize: '1.2rem' }} />
        </button>
      </div>
      <div style={{ ...contentStyle, paddingBottom: '90px' }}>
        <div style={themeGridStyle}>
          {TABLE_THEME_ORDER.map((id) => {
            const t = TABLE_THEMES[id];
            const isSelected = draftTheme === id;
            return (
              <button
                key={id}
                onClick={() => setDraftTheme(id)}
                style={{
                  ...themeCardStyle,
                  borderColor: isSelected ? '#E98C26' : 'rgba(255,255,255,0.08)',
                  boxShadow: isSelected ? '0 0 0 1px #E98C26' : 'none'
                }}
              >
                <div
                  style={{
                    ...themePreviewStyle,
                    backgroundColor: t.swatch.outer
                  }}
                >
                  <div
                    style={{
                      ...themePreviewOvalStyle,
                      backgroundColor: t.swatch.felt,
                      boxShadow: `0 0 0 1px ${t.swatch.ring}, inset 0 0 12px rgba(0,0,0,0.4)`
                    }}
                  />
                </div>
                <span style={themeNameStyle}>{t.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={themeSaveBarStyle}>
        <button
          onClick={() => {
            tableSettings.setTheme(draftTheme);
            setSubView('tableSettings');
          }}
          style={themeSaveBtn}
          disabled={draftTheme === tableSettings.theme}
        >
          Save changes
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      {/* Lock to portrait on mobile-landscape — this panel portals to <body>,
          outside the lobby's wrapper, so the rotation lives inside the portal
          (mirrors AccountMenuPanel, which renders this as a sub-view). */}
      <ForcePortrait>
        <div ref={backdropRef} style={backdropStyle} onClick={onClose} />
        <div ref={panelRef} style={isPortrait ? panelMobile : panelDesktop}>
          {subView === 'tableSettings' && tableSettingsView}
          {subView === 'tableTheme' && tableThemeView}
          {subView === null && (
            <>
              {/* Header — embedded mode shows back arrow + centered title; standalone mode shows
            avatar+name+ID with close X. */}
              {embedded ? (
                <div style={embeddedHeaderStyle}>
                  <button
                    onClick={onClose}
                    style={embeddedBackBtn}
                    type='button'
                    aria-label='Back to menu'
                    {...hoverStyle(
                      { color: '#fff', background: 'rgba(255,255,255,0.08)' },
                      { color: 'rgba(255,255,255,0.7)', background: 'transparent' }
                    )}
                  >
                    <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
                  </button>
                  <span style={embeddedTitleStyle}>Account Profile</span>
                  <span style={{ width: 28 }} aria-hidden />
                </div>
              ) : (
                <div style={headerStyle}>
                  <div style={avatarSection}>
                    <AvatarFrame tier={streakFrame} size={HEADER_AVATAR_SIZE}>
                      <div style={streakFrame ? { ...avatarWrap, border: 'none' } : avatarWrap}>
                        {user.playerAvatar ? (
                          <img
                            src={`https://media.${APP_DOMAIN}/images/${user.playerAvatar}`}
                            alt=''
                            style={avatarImg}
                          />
                        ) : (
                          <PersonIcon
                            style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.4)' }}
                          />
                        )}
                        {activeTab === 'profile' && (
                          <button
                            type='button'
                            onClick={() => {
                              setAvatarMessage('');
                              setIsAvatarPickerOpen(true);
                            }}
                            style={avatarEditBtn}
                            aria-label='Change Avatar'
                          >
                            <EditIcon style={{ fontSize: '0.8rem' }} />
                          </button>
                        )}
                      </div>
                    </AvatarFrame>
                    <div style={headerInfo}>
                      <span style={displayNameStyle}>
                        {user.displayName || user.playerName || 'Player'}
                      </span>
                      <span style={guidStyle}>ID: {user.playerGUID}</span>
                    </div>
                  </div>
                  <button onClick={onClose} style={closeBtn}>
                    <CloseIcon style={{ fontSize: '1.2rem' }} />
                  </button>
                </div>
              )}

              {/* Tabs — hidden in embedded mode (flat scrollable list instead). */}
              {!embedded && (
                <div style={tabsRow}>
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      style={{ ...tabBtn, ...(activeTab === tab.key ? tabActive : {}) }}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Embedded mode: avatar + CHANGE AVATAR button above the field list. */}
              {embedded && (
                <div style={embeddedAvatarBlock}>
                  <AvatarFrame tier={streakFrame} size={EMBEDDED_AVATAR_SIZE}>
                    <button
                      type='button'
                      onClick={() => {
                        setAvatarMessage('');
                        setIsAvatarPickerOpen(true);
                      }}
                      style={
                        streakFrame ? { ...embeddedAvatarWrap, border: 'none' } : embeddedAvatarWrap
                      }
                      aria-label='Change Avatar'
                      {...hoverStyle(
                        {
                          borderColor: 'rgba(233,140,38,1)',
                          boxShadow: '0 0 30px rgba(233,140,38,0.55)'
                        },
                        {
                          borderColor: 'rgba(233,140,38,0.85)',
                          boxShadow: '0 0 24px rgba(233,140,38,0.35)'
                        }
                      )}
                    >
                      {user.playerAvatar ? (
                        <img
                          src={`https://media.${APP_DOMAIN}/images/${user.playerAvatar}`}
                          alt=''
                          style={avatarImg}
                        />
                      ) : (
                        <PersonIcon style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.4)' }} />
                      )}
                    </button>
                  </AvatarFrame>
                  <button
                    type='button'
                    onClick={() => {
                      setAvatarMessage('');
                      setIsAvatarPickerOpen(true);
                    }}
                    style={changeAvatarBtnStyle}
                    {...hoverStyle(
                      { color: saloonTokensRef.orange300, textDecoration: 'underline' },
                      { color: '#E98C26', textDecoration: 'none' }
                    )}
                  >
                    CHANGE AVATAR
                  </button>
                  {avatarMessage && (
                    <span
                      style={{
                        ...avatarMessageBase,
                        color: avatarMessageType === 'error' ? '#c0392b' : '#4CAF50'
                      }}
                    >
                      {avatarMessage}
                    </span>
                  )}
                </div>
              )}

              {/* Content */}
              <div style={contentStyle}>
                {profileMessage && (
                  <div
                    style={{
                      ...flashMessageStyle,
                      ...(profileMessageType === 'error' ? flashErrorStyle : flashSuccessStyle)
                    }}
                    role={profileMessageType === 'error' ? 'alert' : 'status'}
                  >
                    {profileMessage}
                  </div>
                )}

                {loading && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 0',
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '0.8rem'
                    }}
                  >
                    Loading profile...
                  </div>
                )}

                {/* Profile Tab */}
                {!loading && !embedded && activeTab === 'profile' && (
                  <div style={sectionCol}>
                    <FieldRow
                      label='Display Name'
                      value={formData.displayName}
                      editing={editing === 'displayName'}
                      onEdit={() => handleEdit('displayName')}
                      onChange={(v) => {
                        keepDisplayNameFailureVisibleWhileActive();
                        setFormData({ ...formData, displayName: v });
                      }}
                      onSave={() => handleSave('displayName')}
                      saving={savingField === 'displayName'}
                      checking={displayNameChecking}
                      message={showDisplayNameFeedback ? displayNameMessage : ''}
                      messageType={displayNameMessageType}
                      suggestions={showDisplayNameFeedback ? displayNameSuggestions : []}
                      onSuggestionSelect={(suggestion) => {
                        keepDisplayNameFailureVisibleWhileActive();
                        setFormData({ ...formData, displayName: suggestion });
                        setEditing('displayName');
                      }}
                    />
                    <FieldRow
                      label='First Name'
                      value={formData.firstName}
                      placeholder='Not set'
                      editing={editing === 'firstName'}
                      onEdit={() => handleEdit('firstName')}
                      onChange={(v) => setFormData({ ...formData, firstName: v })}
                      onSave={() => handleSave('firstName')}
                      saving={savingField === 'firstName'}
                    />
                    <FieldRow
                      label='Last Name'
                      value={formData.lastName}
                      placeholder='Not set'
                      editing={editing === 'lastName'}
                      onEdit={() => handleEdit('lastName')}
                      onChange={(v) => setFormData({ ...formData, lastName: v })}
                      onSave={() => handleSave('lastName')}
                      saving={savingField === 'lastName'}
                    />
                    <FieldRow
                      label='Email'
                      value={formData.email}
                      readOnly
                      badge={emailVerified ? 'Verified' : undefined}
                    />
                    <FieldRow
                      label='Phone'
                      value={formData.phone}
                      placeholder='Not set'
                      editing={editing === 'phone'}
                      onEdit={() => handleEdit('phone')}
                      onChange={(v) => {
                        // Allow only digits and a leading '+'; strip everything else
                        const sanitized = v.replace(/[^+\d]/g, '').replace(/(?!^)\+/g, '');
                        setFormData({ ...formData, phone: sanitized });
                        setPhoneSaveStatus('');
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.phone;
                          return next;
                        });
                      }}
                      onSave={() => handleSave('phone')}
                      saving={savingField === 'phone'}
                      checking={savingField === 'phone'}
                      checkingMessage={phoneSaveStatus}
                      errorMessage={fieldErrors.phone}
                      badge={formData.phone && phoneVerified ? 'Verified' : undefined}
                    />
                    <PhoneVerificationInline
                      state={phoneVerification}
                      compact={isPortrait}
                      onCodeChange={(code) =>
                        setPhoneVerification((prev) =>
                          prev ? { ...prev, code, error: '', message: '' } : prev
                        )
                      }
                      onVerify={verifyPhoneCode}
                      onResend={resendPhoneCode}
                      onClose={() => {
                        if (!phoneVerification?.verifying && !phoneVerification?.resending) {
                          setFormData((prev) => ({ ...prev, phone: profile.phone }));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.phone;
                            return next;
                          });
                          setPhoneVerification(null);
                        }
                      }}
                    />
                    <FieldRow label='Club' value={clubValue} readOnly />

                    <div style={divider} />
                    <span style={sectionTitle}>Address</span>

                    <FieldRow
                      label='Home Address'
                      value={formData.streetAddress}
                      placeholder='Not set'
                      editing={editing === 'streetAddress'}
                      onEdit={() => editAddressField('streetAddress')}
                      onChange={(v) => updateAddressField('streetAddress', v)}
                      onSave={() => handleAddressSave('streetAddress')}
                      saving={addressSaving && editing === 'streetAddress'}
                    />
                    <FieldRow
                      label='Postal Address'
                      value={formData.postalAddress}
                      placeholder='Not set'
                      editing={editing === 'postalAddress'}
                      onEdit={() => editAddressField('postalAddress')}
                      onChange={(v) => updateAddressField('postalAddress', v)}
                      onSave={() => handleAddressSave('postalAddress')}
                      saving={addressSaving && editing === 'postalAddress'}
                    />
                    <FieldRow
                      label='City'
                      value={formData.city}
                      placeholder='Not set'
                      editing={editing === 'city'}
                      onEdit={() => editAddressField('city')}
                      onChange={(v) => updateAddressField('city', v)}
                      onSave={() => handleAddressSave('city')}
                      saving={addressSaving && editing === 'city'}
                    />
                    <div style={twoCol}>
                      <FieldRow
                        label='State'
                        value={formData.state}
                        placeholder='—'
                        editing={editing === 'state'}
                        onEdit={() => editAddressField('state')}
                        onChange={(v) => updateAddressField('state', v)}
                        onSave={() => handleAddressSave('state')}
                        saving={addressSaving && editing === 'state'}
                      />
                      <FieldRow
                        label='ZIP'
                        value={formData.zipCode}
                        placeholder='—'
                        editing={editing === 'zipCode'}
                        onEdit={() => editAddressField('zipCode')}
                        onChange={(v) => updateAddressField('zipCode', v)}
                        onSave={() => handleAddressSave('zipCode')}
                        saving={addressSaving && editing === 'zipCode'}
                      />
                    </div>
                    <FieldRow
                      label='Country'
                      value={formData.countryCode}
                      displayValue={getCountryLabel(formData.countryCode)}
                      editing={editing === 'countryCode'}
                      onEdit={() => editAddressField('countryCode')}
                      onChange={(v) => updateAddressField('countryCode', v)}
                      onSave={() => handleAddressSave('countryCode')}
                      saving={addressSaving && editing === 'countryCode'}
                      options={countryOptions.map((country) => ({
                        value: country.code,
                        label: country.label
                      }))}
                    />
                    {addressSaveError && (
                      <div style={inlineError} role='alert'>
                        {addressSaveError}
                      </div>
                    )}
                    {addressSaveSuccess && (
                      <div style={inlineSuccess} role='status'>
                        {addressSaveSuccess}
                      </div>
                    )}

                    <div style={divider} />
                    <span style={sectionTitle}>Crypto</span>

                    <FieldRow
                      label='Bitcoin Wallet Address'
                      fieldKey='withdrawalWalletAddress'
                      value={formData.withdrawalWalletAddress}
                      placeholder='Not set'
                      editing={editing === 'withdrawalWalletAddress'}
                      highlighted={
                        highlightedField === 'withdrawalWalletAddress' ||
                        editing === 'withdrawalWalletAddress' ||
                        savingField === 'withdrawalWalletAddress'
                      }
                      saving={savingField === 'withdrawalWalletAddress'}
                      savedMessage={
                        savedField === 'withdrawalWalletAddress' ? 'Successfully saved' : undefined
                      }
                      errorMessage={fieldErrors.withdrawalWalletAddress}
                      onEdit={() => handleEdit('withdrawalWalletAddress')}
                      onChange={(v) => {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.withdrawalWalletAddress;
                          return next;
                        });
                        setFormData({ ...formData, withdrawalWalletAddress: v });
                      }}
                      onSave={() => handleSave('withdrawalWalletAddress')}
                    />
                  </div>
                )}

                {/* Embedded mode: saloon-styled flat field list (see EN-9838). */}
                {!loading && embedded && (
                  <div style={embeddedListStyle}>
                    <EmbeddedRow
                      label='Display Name'
                      value={formData.displayName}
                      editing={editing === 'displayName'}
                      onEdit={() => handleEdit('displayName')}
                      onCancel={() => {
                        handleCancel('displayName');
                        clearDisplayNameFeedback();
                      }}
                      onChange={(v) => {
                        keepDisplayNameFailureVisibleWhileActive();
                        setFormData({ ...formData, displayName: v });
                      }}
                      onSave={() => handleSave('displayName')}
                      saving={savingField === 'displayName'}
                      helperText='*Display name can only be changed once per 30 days.'
                      errorMessage={
                        showDisplayNameFeedback && displayNameMessageType === 'error'
                          ? displayNameMessage
                          : undefined
                      }
                    />
                    {/* First/Last name are identity fields — display-only in the
                  embedded (Claude design) view; no inline EDIT (see EN-9838 QA). */}
                    <EmbeddedRow label='First Name' value={formData.firstName || 'Not set'} />
                    <EmbeddedRow label='Last Name' value={formData.lastName || 'Not set'} />
                    <EmbeddedRow
                      label='Bitcoin Wallet Address'
                      value={formData.withdrawalWalletAddress || 'Not set'}
                      editing={editing === 'withdrawalWalletAddress'}
                      onEdit={() => handleEdit('withdrawalWalletAddress')}
                      onCancel={() => handleCancel('withdrawalWalletAddress')}
                      onChange={(v) => setFormData({ ...formData, withdrawalWalletAddress: v })}
                      onSave={() => handleSave('withdrawalWalletAddress')}
                      saving={savingField === 'withdrawalWalletAddress'}
                      errorMessage={fieldErrors.withdrawalWalletAddress}
                    />
                    {(() => {
                      const addressFieldsList: AddressFieldKey[] = [
                        'streetAddress',
                        'city',
                        'state',
                        'zipCode',
                        'countryCode'
                      ];
                      const isEditingAddress = addressFieldsList.includes(
                        editing as AddressFieldKey
                      );
                      const combinedAddress =
                        [
                          formData.streetAddress,
                          formData.city,
                          formData.zipCode,
                          formData.state,
                          formData.countryCode
                        ]
                          .filter(Boolean)
                          .join(' ') || 'Not set';
                      if (!isEditingAddress) {
                        return (
                          <EmbeddedRow
                            label='Address'
                            value={combinedAddress}
                            onEdit={() => editAddressField('streetAddress')}
                            editing={false}
                          />
                        );
                      }
                      // Browser autofill / password-manager suppression attributes —
                      // these are not standard React props so we spread them as `any`.
                      const noAutofill: any = {
                        autoComplete: 'off',
                        autoCorrect: 'off',
                        autoCapitalize: 'off',
                        spellCheck: false,
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                        'data-form-type': 'other'
                      };
                      return (
                        <>
                          {/* Header row: "ADDRESS" label + CANCEL link, no bottom border so
                        the subfield rows below flow as a single editing block. */}
                          <div
                            style={{
                              ...embeddedRowStyle,
                              borderBottom: 'none',
                              paddingBottom: 4
                            }}
                          >
                            <div style={embeddedRowHeader}>
                              <span style={embeddedRowLabel}>Address</span>
                              <button
                                type='button'
                                onClick={handleCancelAddress}
                                style={embeddedEditLink}
                                disabled={addressSaving}
                                {...hoverStyle(
                                  { color: saloonTokensRef.orange300, textDecoration: 'underline' },
                                  { color: saloonTokensRef.orange300, textDecoration: 'none' }
                                )}
                              >
                                CANCEL
                              </button>
                            </div>
                          </div>
                          <EmbeddedSubInputRow
                            label='Street'
                            value={formData.streetAddress}
                            onChange={(v) => updateAddressField('streetAddress', v)}
                            disabled={addressSaving}
                            autoFocus
                            inputProps={noAutofill}
                          />
                          <EmbeddedSubInputRow
                            label='City'
                            value={formData.city}
                            onChange={(v) => updateAddressField('city', v)}
                            disabled={addressSaving}
                            inputProps={noAutofill}
                          />
                          <EmbeddedSubInputRow
                            label='State'
                            value={formData.state}
                            onChange={(v) => updateAddressField('state', v)}
                            disabled={addressSaving}
                            inputProps={noAutofill}
                          />
                          <EmbeddedSubInputRow
                            label='ZIP'
                            value={formData.zipCode}
                            onChange={(v) => updateAddressField('zipCode', v)}
                            disabled={addressSaving}
                            inputProps={noAutofill}
                          />
                          <div
                            style={{
                              ...embeddedRowStyle,
                              borderBottom: 'none',
                              paddingTop: 6
                            }}
                          >
                            <span style={embeddedRowLabel}>Country</span>
                            <select
                              value={formData.countryCode}
                              onChange={(e) => updateAddressField('countryCode', e.target.value)}
                              style={embeddedSelectStyle}
                              disabled={addressSaving}
                            >
                              {countryOptions.map((c) => (
                                <option key={c.code} value={c.code}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type='button'
                              onClick={() => handleAddressSave('streetAddress')}
                              style={{
                                ...embeddedSaveBtn,
                                alignSelf: 'flex-start',
                                marginTop: 12
                              }}
                              disabled={addressSaving}
                              {...hoverStyle(
                                { background: saloonTokensRef.orange300 },
                                { background: saloonTokensRef.orange500 }
                              )}
                            >
                              {addressSaving ? 'SAVING…' : 'SAVE ADDRESS'}
                            </button>
                          </div>
                        </>
                      );
                    })()}
                    <EmbeddedRow label='Your Club' value={clubValue} italic={!profile.clubName} />
                    <EmbeddedRow
                      label='Email Address'
                      value={formData.email || user.playerEmail || 'Not set'}
                      verified={!!emailVerified}
                    />
                    <EmbeddedRow
                      label='Phone Number'
                      value={
                        editing === 'phone'
                          ? formData.phone || ''
                          : formData.phone
                          ? formatPhoneDisplay(formData.phone)
                          : 'Not set'
                      }
                      verified={formData.phone ? !!phoneVerified : undefined}
                      editing={editing === 'phone'}
                      onEdit={() => handleEdit('phone')}
                      onCancel={() => {
                        // Revert the typed-but-discarded number to the saved one
                        // (and clear any in-progress OTP state) instead of leaving
                        // the draft in the field.
                        revertPhoneToSaved('phone', phoneVerification, profile.phone);
                        setEditing(null);
                      }}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          phone: v.replace(/[^+\d]/g, '').replace(/(?!^)\+/g, '')
                        })
                      }
                      onSave={() => handleSave('phone')}
                      saving={savingField === 'phone'}
                      editLabel='Change Number'
                      errorMessage={fieldErrors.phone}
                    />
                    <PhoneVerificationInline
                      state={phoneVerification}
                      compact
                      onCodeChange={(code) =>
                        setPhoneVerification((prev) =>
                          prev ? { ...prev, code, error: '', message: '' } : prev
                        )
                      }
                      onVerify={verifyPhoneCode}
                      onResend={resendPhoneCode}
                      onClose={() => {
                        if (!phoneVerification?.verifying && !phoneVerification?.resending) {
                          setFormData((prev) => ({ ...prev, phone: profile.phone }));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.phone;
                            return next;
                          });
                          setPhoneVerification(null);
                        }
                      }}
                    />
                    <EmbeddedRow
                      label='ID Document'
                      value={idDocumentValue}
                      verified={idStatus === 'APPROVED'}
                    />
                    <EmbeddedRow
                      label='Identity'
                      value={identityValue}
                      valueStyle={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#d9c79e', // var(--bone-2)
                        opacity: 0.85,
                        letterSpacing: '0.005em',
                        lineHeight: 1.5
                      }}
                    />
                    <EmbeddedRow
                      label='Total Fees Today'
                      value={`$${profile.totalFeesToday.toFixed(2)}`}
                      valueStyle={{ fontSize: '17px', fontWeight: 700 }}
                      valueNode={
                        <>
                          <span style={{ color: saloonTokensRef.orange500 }}>$</span>
                          {profile.totalFeesToday.toFixed(2)}
                        </>
                      }
                    />
                    {/* Change Password — the embedded profile (the slide-in panel
                      users actually see) had no password action; only the
                      non-embedded Security tab did. Reuse the row's edit-link
                      affordance (like Phone's "Change Number") to open the
                      shared ChangePasswordModal mounted at the panel root.
                      Hidden for social-auth (Google/Apple) accounts, which have
                      no app-managed password (Unity parity). */}
                    {!profile.isSocialAuth && (
                      <EmbeddedRow
                        label='Password'
                        value='••••••••'
                        editLabel='Change Password'
                        onEdit={() => setShowChangePasswordModal(true)}
                      />
                    )}
                    {addressSaveError && (
                      <div style={inlineError} role='alert'>
                        {addressSaveError}
                      </div>
                    )}
                    {addressSaveSuccess && (
                      <div style={inlineSuccess} role='status'>
                        {addressSaveSuccess}
                      </div>
                    )}
                  </div>
                )}

                {/* Security Tab */}
                {!loading && !embedded && activeTab === 'security' && (
                  <div style={sectionCol}>
                    {/* Hidden for social-auth (Google/Apple) accounts — no
                      app-managed password to change (Unity parity). */}
                    {!profile.isSocialAuth && (
                      <button style={actionCard} onClick={() => setShowChangePasswordModal(true)}>
                        <LockIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
                        <div style={actionCardInfo}>
                          <span style={actionCardLabel}>Change Password</span>
                          <span style={actionCardDesc}>Update your login password</span>
                        </div>
                      </button>
                    )}

                    {/* Biometrics toggle — every signed-in player, whatever
                      their login method (AC11a). The stored credential is the
                      session's refresh token, not a password, so every
                      account type has the same thing to unlock.
                      Previously gated on sensor availability, which
                      made the feature ABSENT (not off) on any device with no
                      face/finger enrolled — undiscoverable from Settings, and
                      the login offer clears itself in silence on the same
                      devices. The row now stays, says why it's blocked, and
                      a blocked tap opens the explainer with the setup path
                      instead of toggling (AC12: three states, three words).
                      Needs an email: the credential is keyed by it and
                      handleBiometricToggle bails without one (phone-only
                      OTP accounts), matching the auth app's offer gate. */}
                    {!!user.playerEmail && (
                      <button
                        style={{
                          ...actionCard,
                          opacity: isEnrolling || biometricBlocked === 'unsupported' ? 0.6 : 1
                        }}
                        onClick={
                          biometricBlocked
                            ? () => setShowBiometricHelp(true)
                            : handleBiometricToggle
                        }
                        disabled={isEnrolling || biometricBlocked === 'unsupported'}
                      >
                        {isEnrolling ? (
                          <CircularProgress size={18} style={{ color: '#E98C26', flexShrink: 0 }} />
                        ) : (
                          // The device's actual sensor, in the same neutral the
                          // row's siblings use — state lives in the ON/OFF pill,
                          // not in a green icon (owner 2026-08-20).
                          <span
                            style={{
                              color: 'rgba(255,255,255,0.75)',
                              display: 'flex',
                              flexShrink: 0
                            }}
                          >
                            <SensorGlyph kind={biometricLabelKind} size={18} />
                          </span>
                        )}
                        <div style={actionCardInfo}>
                          <span style={actionCardLabel}>Biometric Login</span>
                          <span style={actionCardDesc}>
                            {isEnrolling
                              ? 'Setting up biometrics…'
                              : biometricBlocked === 'not-enrolled'
                              ? `${biometryTitle(
                                  biometricLabelKind
                                )} isn’t set up on this device — tap to see how`
                              : biometricBlocked === 'locked-out'
                              ? 'Locked after too many attempts — unlock this device with its passcode'
                              : biometricBlocked === 'unsupported'
                              ? isNativeApp
                                ? 'This device has no fingerprint or face sensor'
                                : 'This browser can’t use biometric sign-in'
                              : passkeyEnrolled
                              ? 'Enabled — tap to disable on this device'
                              : 'Not enabled on this device'}
                          </span>
                        </div>
                        <span
                          style={{
                            ...biometricTogglePill,
                            ...(passkeyEnrolled && !biometricBlocked
                              ? biometricTogglePillOn
                              : biometricTogglePillOff)
                          }}
                        >
                          {passkeyEnrolled && !biometricBlocked ? 'ON' : 'OFF'}
                        </span>
                      </button>
                    )}
                    {showBiometricHelp && biometricBlocked && (
                      <BiometricSurfaceHost
                        onDismiss={() => setShowBiometricHelp(false)}
                        ariaLabel='Biometric sign-in unavailable'
                      >
                        <BiometricUnavailableCard
                          skin='hijack'
                          biometry={biometricLabelKind}
                          reason={biometricBlocked}
                          onAction={() => {
                            if (biometricBlocked === 'not-enrolled') openIOSSettings();
                            setShowBiometricHelp(false);
                          }}
                          onDismiss={() => setShowBiometricHelp(false)}
                        />
                      </BiometricSurfaceHost>
                    )}

                    <button style={actionCard}>
                      <PhoneAndroidIcon style={{ fontSize: '1.1rem', color: '#63b3ed' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Phone Verification</span>
                        <span style={actionCardDesc}>
                          {phoneVerified ? 'Verified' : 'Add phone for 2FA'}
                        </span>
                      </div>
                      {phoneVerified ? (
                        <CheckCircleIcon style={{ fontSize: '1rem', color: '#4CAF50' }} />
                      ) : (
                        <PendingIcon style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                      )}
                    </button>

                    <button style={actionCard}>
                      <EmailIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Email Verification</span>
                        <span style={actionCardDesc}>
                          {emailVerified ? user.playerEmail : 'Verify your email'}
                        </span>
                      </div>
                      {emailVerified ? (
                        <CheckCircleIcon style={{ fontSize: '1rem', color: '#4CAF50' }} />
                      ) : (
                        <PendingIcon style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }} />
                      )}
                    </button>
                  </div>
                )}

                {/* Verification Tab */}
                {!loading && !embedded && activeTab === 'verification' && (
                  <div style={sectionCol}>
                    <div style={verificationCard}>
                      <VerifiedUserIcon
                        style={{ fontSize: '2rem', color: getStatusColor(idStatus) }}
                      />
                      <span style={verStatusLabel}>Identity Verification</span>
                      <div
                        style={{
                          ...verStatusBadge,
                          color: getStatusColor(idStatus),
                          backgroundColor: getStatusBg(idStatus)
                        }}
                      >
                        {idStatus}
                      </div>
                      <span style={verStatusDesc}>
                        {idStatus === 'APPROVED' && 'Your identity has been verified.'}
                        {idStatus === 'PENDING' &&
                          'Please complete identity verification to increase your deposit limits.'}
                        {idStatus === 'REVIEWING' &&
                          'Your documents are being reviewed. This usually takes 1-2 business days.'}
                        {idStatus === 'DENIED' &&
                          'Your verification was denied. Please contact support.'}
                        {idStatus === 'SUSPECTED' &&
                          'Your account is under review. Please contact support.'}
                      </span>
                      {(idStatus === 'PENDING' || idStatus === 'DENIED') && (
                        <button style={verifyBtn}>
                          <HomeIcon style={{ fontSize: '0.9rem' }} />
                          Start Verification
                        </button>
                      )}
                    </div>

                    <div style={divider} />

                    <div style={profileDetailsCard}>
                      <FieldRow
                        label='ID Document'
                        value={idDocumentValue}
                        readOnly
                        badge={getStatusLabel(idStatus)}
                        badgeColor={getStatusColor(idStatus)}
                        badgeBg={getStatusBg(idStatus)}
                      />
                      <FieldRow
                        label='Identity'
                        value={identityValue}
                        readOnly
                        badge={idStatus === 'APPROVED' ? 'Jumio' : undefined}
                        badgeColor='#4CAF50'
                        badgeBg='rgba(76,175,80,0.15)'
                      />
                      <div style={feeRow}>
                        <span style={fieldLabel}>Total Fees Today</span>
                        <span style={feeValue}>
                          <span style={feeCurrency}>$</span>
                          {profile.totalFeesToday.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Settings Tab */}
                {!loading && !embedded && activeTab === 'settings' && (
                  <div style={sectionCol}>
                    <button
                      style={actionCard}
                      onClick={() => {
                        setDraftTheme(tableSettings.theme);
                        setSubView('tableSettings');
                      }}
                    >
                      <TableRestaurantIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Table Settings</span>
                        <span style={actionCardDesc}>Theme, chat, and table sounds</span>
                      </div>
                      <ChevronRightIcon
                        style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.3)' }}
                      />
                    </button>

                    {systemLogAvailable && (
                      <button
                        style={actionCard}
                        onClick={() => {
                          setDebugConsoleEnabled(user.playerGUID, !debugConsoleOn);
                        }}
                      >
                        <TerminalIcon
                          style={{
                            fontSize: '1.1rem',
                            color: debugConsoleOn ? '#4CAF50' : 'rgba(255,255,255,0.3)'
                          }}
                        />
                        <div style={actionCardInfo}>
                          <span style={actionCardLabel}>System Log</span>
                          <span style={actionCardDesc}>
                            {debugConsoleOn ? 'Enabled — tap to hide' : 'Disabled'}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                )}

                {/* More Tab */}
                {!loading && !embedded && activeTab === 'more' && (
                  <div style={sectionCol}>
                    <span style={sectionTitle}>Quick Links</span>

                    <button style={actionCard} onClick={() => openExternalUrl(PRICING_URL)}>
                      <AttachMoneyIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Pricing</span>
                        <span style={actionCardDesc}>View Hijack table rates and fees</span>
                      </div>
                    </button>

                    <button
                      style={actionCard}
                      onClick={() => openExternalUrl(TOURNAMENT_SCHEDULE_URL)}
                    >
                      <EventIcon style={{ fontSize: '1.1rem', color: '#63b3ed' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Tournament Schedule</span>
                        <span style={actionCardDesc}>Open the current tournament schedule</span>
                      </div>
                    </button>

                    <button style={actionCard} onClick={() => openExternalUrl(PROMOS_URL)}>
                      <LocalOfferIcon style={{ fontSize: '1.1rem', color: '#E98C26' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Promos</span>
                        <span style={actionCardDesc}>Browse current Hijack promotions</span>
                      </div>
                    </button>

                    <button style={actionCard} onClick={() => openExternalUrl(HELP_CENTER_URL)}>
                      <MenuBookIcon style={{ fontSize: '1.1rem', color: '#d4a853' }} />
                      <div style={actionCardInfo}>
                        <span style={actionCardLabel}>Knowledge Base</span>
                        <span style={actionCardDesc}>Open the Hijack help center</span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Logout — hidden in embedded mode; the outer AccountMenuPanel owns it. */}
                {!embedded && (
                  <>
                    <div style={divider} />
                    <button
                      onClick={() => {
                        clearAuth();
                        clearPostLoginState();
                        window.location.reload();
                      }}
                      style={logoutBtn}
                    >
                      <LogoutIcon style={{ fontSize: '1rem' }} />
                      Log Out
                    </button>
                    <VersionLabel style={versionFooter} />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </ForcePortrait>
      {streaksEnabled && streakSnapshot ? (
        <StreakAvatarPicker
          open={isAvatarPickerOpen}
          snapshot={streakSnapshot}
          currentAvatar={user.playerAvatar}
          onClose={() => setIsAvatarPickerOpen(false)}
          onSave={saveStreakAvatarChange}
        />
      ) : (
        <AvatarPickerModal
          open={isAvatarPickerOpen}
          currentAvatar={user.playerAvatar}
          saving={avatarSaving}
          message={avatarMessage}
          messageType={avatarMessageType}
          onClose={() => {
            if (!avatarSaving) setIsAvatarPickerOpen(false);
          }}
          onConfirm={handleAvatarConfirm}
          onSelect={() => setAvatarMessage('')}
        />
      )}
      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>,
    document.body
  );
};

/* ─── Embedded-mode row (saloon design) ─── */
const saloonTokensRef = {
  orange500: '#e8821f',
  orange300: '#ffb45c',
  bone: '#f1e3c7',
  bone2: '#d9c79e',
  bone3: 'rgba(241,227,199,0.45)',
  divider: 'rgba(232,130,31,0.18)'
};

// Hover helper for inline-styled controls (these use CSSProperties, not CSS
// classes, so :hover isn't available). Applies `hover` styles on pointer enter
// and restores `rest` on leave (see EN-9838 follow-up: profile controls had no
// hover feedback).
const hoverStyle = (hover: Partial<CSSStyleDeclaration>, rest: Partial<CSSStyleDeclaration>) => ({
  onMouseEnter: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onMouseLeave: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  // Touch devices don't fire hover — mirror it onto touch so taps give the
  // same visual feedback on mobile.
  onTouchStart: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onTouchEnd: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  onTouchCancel: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest)
});

const EmbeddedRow = ({
  label,
  value,
  editing = false,
  onEdit,
  onCancel,
  onChange,
  onSave,
  saving = false,
  helperText,
  verified,
  editLabel = 'Edit',
  errorMessage,
  italic = false,
  valueColor,
  valueStyle,
  valueNode
}: {
  label: string;
  value: string;
  editing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onChange?: (v: string) => void;
  onSave?: () => void;
  saving?: boolean;
  helperText?: string;
  verified?: boolean;
  editLabel?: string;
  errorMessage?: string;
  italic?: boolean;
  valueColor?: string;
  valueStyle?: CSSProperties;
  valueNode?: ReactNode;
}) => {
  return (
    <div style={embeddedRowStyle}>
      <div style={embeddedRowHeader}>
        <span style={embeddedRowLabel}>{label}</span>
        {(verified !== undefined || onEdit) && (
          <div style={embeddedRowActions}>
            {verified === true && (
              <span style={embeddedVerifiedStyle}>
                <CheckCircleOutlineIcon style={{ fontSize: '1.1rem' }} /> VERIFIED
              </span>
            )}
            {verified === false && (
              <span style={embeddedUnverifiedStyle}>
                <WarningAmberRoundedIcon style={{ fontSize: '1.05rem' }} /> UNVERIFIED
              </span>
            )}
            {onEdit && (
              <button
                type='button'
                onClick={editing ? onCancel : onEdit}
                style={embeddedEditLink}
                disabled={saving}
                {...hoverStyle(
                  { color: saloonTokensRef.orange300, textDecoration: 'underline' },
                  { color: saloonTokensRef.orange300, textDecoration: 'none' }
                )}
              >
                {editing ? 'CANCEL' : editLabel.toUpperCase()}
              </button>
            )}
          </div>
        )}
      </div>
      {editing && onChange && onSave ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          style={embeddedEditWrap}
        >
          <input
            autoFocus
            value={value === 'Not set' ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            style={embeddedInputStyle}
            disabled={saving}
          />
          <button
            type='submit'
            style={embeddedSaveBtn}
            disabled={saving}
            {...hoverStyle(
              { background: saloonTokensRef.orange300 },
              { background: saloonTokensRef.orange500 }
            )}
          >
            {saving ? '…' : 'SAVE'}
          </button>
        </form>
      ) : (
        <span
          style={{
            ...embeddedRowValue,
            // Empty fields render as a dimmed, UPPERCASE placeholder ("NOT SET"),
            // matching the design — distinct from the bright style for real values.
            ...((value || '').trim().toLowerCase() === 'not set' ? embeddedPlaceholderValue : {}),
            // .m-apf-value-muted — dimmed italic variant for empty/placeholder values.
            ...(italic
              ? {
                  fontStyle: 'italic',
                  color: saloonTokensRef.bone2,
                  opacity: 0.65,
                  fontWeight: 500
                }
              : {}),
            ...(valueColor ? { color: valueColor } : {}),
            ...(valueStyle || {})
          }}
        >
          {valueNode || value}
        </span>
      )}
      {errorMessage && <span style={embeddedRowError}>{errorMessage}</span>}
      {helperText && <span style={embeddedHelperStyle}>{helperText}</span>}
    </div>
  );
};

const EmbeddedSubInputRow = ({
  label,
  value,
  onChange,
  disabled,
  autoFocus,
  inputProps
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  inputProps?: Record<string, any>;
}) => {
  return (
    <div style={{ ...embeddedRowStyle, borderBottom: 'none', paddingTop: 6, paddingBottom: 6 }}>
      <span style={embeddedRowLabel}>{label}</span>
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={embeddedInputStyle}
        disabled={disabled}
        {...(inputProps || {})}
      />
    </div>
  );
};

const PhoneVerificationInline = ({
  state,
  compact,
  onCodeChange,
  onVerify,
  onResend,
  onClose
}: {
  state: PhoneVerificationState | null;
  compact?: boolean;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  onResend: () => void;
  onClose: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  if (!state) return null;

  const code = state.code.replace(/\D/g, '').slice(0, 6);
  const isSuccess = state.status === 'success';
  const resendLabel = formatResendSeconds(state.resendSeconds);

  return (
    <div style={phoneVerificationPanel} role='region' aria-label='Phone verification'>
      <div style={phoneVerificationHeader}>
        <PhoneAndroidIcon style={{ fontSize: '1rem', color: '#63b3ed' }} />
        <span style={phoneVerificationTitle}>
          {isSuccess ? 'Phone Verified' : 'Enter 6-digit Code'}
        </span>
        <button
          type='button'
          onClick={onClose}
          style={phoneVerificationCloseBtn}
          aria-label={isSuccess ? 'Close phone verification' : 'Cancel phone verification'}
          disabled={state.verifying}
        >
          <CloseIcon style={{ fontSize: '0.85rem' }} />
        </button>
      </div>
      {isSuccess ? (
        <span style={phoneVerificationText}>Your phone number has been updated successfully.</span>
      ) : (
        <>
          <span style={phoneVerificationText}>We sent a 6-digit code to {state.phone}.</span>
          <div style={{ ...phoneCodeRow, ...(compact ? phoneCodeRowCompact : {}) }}>
            <div
              style={phoneCodeBoxesWrap}
              onClick={() => inputRef.current?.focus()}
              role='presentation'
            >
              <input
                ref={inputRef}
                value={code}
                onChange={(event) =>
                  onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onVerify();
                }}
                style={phoneCodeHiddenInput}
                aria-label='6-digit verification code'
                inputMode='numeric'
                maxLength={6}
                pattern='[0-9]{6}'
                autoComplete='one-time-code'
                autoFocus
              />
              {Array.from({ length: 6 }).map((_, index) => (
                <span
                  key={index}
                  style={{
                    ...phoneCodeBox,
                    ...(code.length === index ? phoneCodeBoxActive : {}),
                    ...(code[index] ? phoneCodeBoxFilled : {})
                  }}
                >
                  {code[index] || ''}
                </span>
              ))}
            </div>
            <button
              type='button'
              onClick={onVerify}
              style={{
                ...phoneVerifyChipBtn,
                ...(code.length === 6 && !state.verifying
                  ? phoneVerifyChipBtnReady
                  : phoneVerifyChipBtnDim)
              }}
              disabled={state.verifying || code.length !== 6}
              aria-label='Verify code'
            >
              <CheckCircleIcon style={{ fontSize: '0.95rem' }} />
              <span>{state.verifying ? 'Verifying…' : 'Verify'}</span>
            </button>
          </div>
          <div style={phoneResendRow}>
            {state.resendSeconds > 0 ? (
              <span style={phoneVerificationText}>Resend available in {resendLabel}</span>
            ) : (
              <button
                type='button'
                onClick={onResend}
                style={{
                  ...phoneResendButton,
                  ...(state.resending ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                }}
                disabled={state.resending}
              >
                {state.resending ? 'Resending...' : 'Resend code'}
              </button>
            )}
          </div>
          {state.message && (
            <span style={phoneVerificationNotice} role='status'>
              {state.message}
            </span>
          )}
          {state.error && (
            <span style={phoneVerificationError} role='alert'>
              {state.error}
            </span>
          )}
        </>
      )}
    </div>
  );
};

const formatResendSeconds = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

/** Action-card row with a pill toggle on the right. */
const ToggleRow = ({
  icon,
  label,
  checked,
  onChange,
  disabled = false
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) => (
  <button
    style={actionCard}
    disabled={disabled}
    aria-pressed={checked}
    onClick={() => onChange(!checked)}
  >
    {icon}
    <div style={actionCardInfo}>
      <span style={actionCardLabel}>{label}</span>
    </div>
    <span
      style={{
        ...togglePillStyle,
        backgroundColor: checked ? '#E98C26' : 'rgba(255,255,255,0.12)'
      }}
    >
      <span
        style={{
          ...toggleKnobStyle,
          transform: `translateX(${checked ? 16 : 0}px)`
        }}
      />
    </span>
  </button>
);

/** Editable field row */
const FieldRow = ({
  label,
  value,
  displayValue,
  placeholder,
  editing,
  readOnly,
  onEdit,
  onChange,
  onSave,
  badge,
  highlighted,
  fieldKey,
  saving,
  savedMessage,
  errorMessage,
  checking,
  checkingMessage,
  message,
  messageType,
  suggestions,
  onSuggestionSelect,
  badgeColor,
  badgeBg,
  options
}: {
  label: string;
  fieldKey?: string;
  value: string;
  displayValue?: string;
  placeholder?: string;
  editing?: boolean;
  readOnly?: boolean;
  onEdit?: () => void;
  onChange?: (v: string) => void;
  onSave?: () => void;
  badge?: string;
  highlighted?: boolean;
  saving?: boolean;
  savedMessage?: string;
  errorMessage?: string;
  checking?: boolean;
  checkingMessage?: string;
  message?: string;
  messageType?: 'success' | 'error';
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  badgeColor?: string;
  badgeBg?: string;
  options?: { value: string; label: string }[];
}) => (
  <div
    style={{ ...fieldRow, ...(highlighted ? fieldRowHighlighted : {}) }}
    data-profile-field={fieldKey}
  >
    <span style={fieldLabel}>{label}</span>
    <div style={fieldValueRow}>
      {editing ? (
        <>
          {options ? (
            <select
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              style={{ ...fieldSelect, ...(highlighted ? fieldInputInHighlightedRow : {}) }}
              autoFocus
              aria-label={label}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value} style={countryOptionStyle}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type='text'
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              style={{ ...fieldInput, ...(highlighted ? fieldInputInHighlightedRow : {}) }}
              autoFocus
              aria-label={label}
            />
          )}
          <button
            onClick={onSave}
            style={{ ...saveBtn, ...(saving ? disabledSaveBtn : {}) }}
            aria-label={saving ? `Saving ${label}` : `Save ${label}`}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={14} thickness={5} style={{ color: '#4CAF50' }} />
            ) : (
              <CheckCircleIcon style={{ fontSize: '1rem' }} />
            )}
          </button>
        </>
      ) : (
        <>
          <span style={value ? fieldValue : fieldPlaceholder}>
            {displayValue || value || placeholder || '—'}
          </span>
          {badge && (
            <span
              style={{
                ...verifiedBadge,
                ...(badgeColor ? { color: badgeColor } : {}),
                ...(badgeBg ? { backgroundColor: badgeBg } : {})
              }}
            >
              {badge}
            </span>
          )}
          {!readOnly && onEdit && (
            <button onClick={onEdit} style={editBtn} aria-label={`Edit ${label}`}>
              <EditIcon style={{ fontSize: '0.8rem' }} />
            </button>
          )}
        </>
      )}
    </div>
    {checking && (
      <span style={{ ...fieldMessage, ...fieldNeutralMessage }} role='status'>
        {checkingMessage || 'Checking...'}
      </span>
    )}
    {message && (
      <span
        style={{
          ...fieldMessage,
          ...(messageType === 'error' ? fieldErrorMessage : fieldSuccessMessage)
        }}
        role={messageType === 'error' ? 'alert' : 'status'}
      >
        {message}
      </span>
    )}
    {!!suggestions?.length && (
      <div style={suggestionsWrap} aria-label={`${label} suggestions`}>
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type='button'
            style={suggestionBtn}
            onClick={() => onSuggestionSelect?.(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>
    )}
    {errorMessage && (
      <span style={fieldErrorText} role='alert'>
        {errorMessage}
      </span>
    )}
    {savedMessage && <span style={savedText}>{savedMessage}</span>}
  </div>
);

const getStatusColor = (s: string) => {
  if (s === 'APPROVED') return '#4CAF50';
  if (s === 'PENDING' || s === 'REVIEWING') return '#E98C26';
  return '#c0392b';
};

const getStatusBg = (s: string) => {
  if (s === 'APPROVED') return 'rgba(76,175,80,0.15)';
  if (s === 'PENDING' || s === 'REVIEWING') return 'rgba(233,140,38,0.15)';
  return 'rgba(200,40,40,0.15)';
};

const getStatusLabel = (status: string) => {
  if (status === 'APPROVED') return 'Approved';
  if (status === 'REVIEWING') return 'Reviewing';
  if (status === 'DENIED') return 'Denied';
  if (status === 'SUSPECTED') return 'Under Review';
  return 'Pending';
};

const formatDocumentType = (value: string) => {
  if (!value) return '';
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getIdentityValue = (reason: string, status: string) => {
  if (reason) return reason;
  if (status === 'APPROVED') return 'Verified';
  if (status === 'REVIEWING') return 'Identity review in progress';
  if (status === 'DENIED') return 'Verification denied';
  if (status === 'SUSPECTED') return 'Account under review';
  return 'Verification pending';
};

const getAddressValidationError = (fields: Record<AddressFieldKey, string>) => {
  const missingField = requiredAddressFields.find((field) => !fields[field]);
  if (missingField) {
    return `${addressFieldLabels[missingField]} is required.`;
  }

  const tooLongField = addressFieldKeys.find(
    (field) => fields[field].length > addressFieldMaxLengths[field]
  );
  if (tooLongField) {
    return `${addressFieldLabels[tooLongField]} must be ${addressFieldMaxLengths[tooLongField]} characters or less.`;
  }

  if (!countryOptions.some((country) => country.code === fields.countryCode)) {
    return 'Select a valid country.';
  }

  return '';
};

export default ProfilePanel;

/* ── Styles ── */

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  zIndex: 900,
  opacity: 0
  // pointerEvents intentionally omitted — the gsap.set() in the open
  // animation flips it to 'auto' so backdrop clicks close the modal
  // (matching CashierPanel's behavior).
};

const panelBase: CSSProperties = {
  position: 'fixed',
  backgroundColor: 'rgba(12,18,28,0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 950,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Honor device safe areas so the header doesn't sit under the notch/status
  // bar and the scrollable content clears the home indicator in portrait.
  ...safeAreaPanelInsets
};
const panelMobile: CSSProperties = {
  ...panelBase,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  // 100% (containing block), not 100vh (device viewport): inside ForcePortrait's
  // rotated box on mobile-landscape the box is 100vw tall, so a viewport-height
  // panel falls short and crops. 100% fills the rotated box; identical to 100vh
  // when not rotated.
  height: '100%',
  boxShadow: '0 -8px 40px rgba(0,0,0,0.5)'
};
const panelDesktop: CSSProperties = {
  ...panelBase,
  top: 0,
  right: 0,
  bottom: 0,
  width: '420px',
  borderTopLeftRadius: '16px',
  borderBottomLeftRadius: '16px',
  boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
  borderLeft: '1px solid rgba(255,255,255,0.06)'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 20px 12px',
  flexShrink: 0
};
const avatarSection: CSSProperties = { display: 'flex', alignItems: 'center', gap: '12px' };

/* ─── Embedded mode (rendered inside AccountMenuPanel) ─── */
const embeddedHeaderStyle: CSSProperties = {
  // Grid (back btn | title | spacer) keeps the title optically centered
  // regardless of its width — flex space-between drifted off-center when the
  // back button and spacer weren't exactly balanced (see EN-9838 QA).
  display: 'grid',
  gridTemplateColumns: '28px 1fr 28px',
  alignItems: 'center',
  padding: '20px 20px 16px',
  // Chunky orange dashed underline — explicit dash/gap sizing via
  // repeating-linear-gradient, which (unlike CSS `border: dashed`)
  // renders identical dash widths across browsers.
  backgroundImage:
    'repeating-linear-gradient(to right, rgba(232,130,31,0.7) 0 9px, transparent 9px 15px)',
  backgroundSize: '100% 2px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom left',
  flexShrink: 0
};
const embeddedBackBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.7)',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 999,
  transition: 'background-color 0.15s ease, color 0.15s ease'
};
const embeddedTitleStyle: CSSProperties = {
  // "Account Profile" header — matches the design's computed style.
  flex: 1,
  textAlign: 'center',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '18px',
  color: '#f1e3c7', // var(--bone)
  letterSpacing: '0.04em'
};
const embeddedAvatarBlock: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  padding: '20px 16px 8px',
  flexShrink: 0
};
// Outer sizes, border included, so the streak frame fits them exactly.
const EMBEDDED_AVATAR_SIZE = 112;
const HEADER_AVATAR_SIZE = 48;

const embeddedAvatarWrap: CSSProperties = {
  boxSizing: 'border-box',
  width: EMBEDDED_AVATAR_SIZE,
  height: EMBEDDED_AVATAR_SIZE,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '3px solid rgba(233,140,38,0.85)',
  boxShadow: '0 0 24px rgba(233,140,38,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  padding: 0,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};
const changeAvatarBtnStyle: CSSProperties = {
  // Same treatment as EDIT / CHANGE NUMBER: Oswald 700 / 13px / orange-300.
  background: 'transparent',
  border: 'none',
  color: '#ffb45c', // var(--orange-300)
  fontFamily: "'Oswald', sans-serif",
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  padding: '2px 6px',
  textShadow: '0 0 5px rgba(232, 130, 31, 0.25)',
  transition: 'color 0.15s ease'
};
const avatarMessageBase: CSSProperties = {
  fontSize: '0.7rem',
  marginTop: 2
};

/* Embedded-mode field list — matches the saloon design from EN-9838. */
const embeddedListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '4px 18px 16px',
  width: '100%'
};
const embeddedRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '14px 0',
  borderBottom: '1px dashed rgba(232,130,31,0.18)'
};
const embeddedRowHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8
};
const embeddedRowLabel: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.7rem',
  letterSpacing: '0.18em',
  color: '#d9c79e',
  textTransform: 'uppercase'
};
const embeddedRowValue: CSSProperties = {
  // Matches the design's computed value style exactly (Saloon "display" text):
  // Oswald 600 / 15px / bone (var(--bone)) / 0.01em / line-height 1.35.
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  fontSize: '15px',
  color: '#f1e3c7', // var(--bone)
  letterSpacing: '0.01em',
  lineHeight: 1.35,
  wordBreak: 'break-word'
};
// Empty/unset fields ("NOT SET") — matches the design's computed placeholder:
// Special Elite 400 / 12px / bone-2 / opacity 0.55 / 0.12em, uppercase.
const embeddedPlaceholderValue: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontWeight: 400,
  fontSize: '12px',
  color: '#d9c79e', // var(--bone-2)
  opacity: 0.55,
  letterSpacing: '0.12em',
  textTransform: 'uppercase'
};

// Display-format a phone number to match the design (e.g. "1-512-981-8229").
// Pure presentation only — the raw digits in formData.phone are unchanged.
function formatPhoneDisplay(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `${digits[0]}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Non-US / unexpected length → show as entered.
  return raw;
}
const embeddedHelperStyle: CSSProperties = {
  // Design renders the disclaimer in the typewriter face (upright), not Inter italic.
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.7rem',
  letterSpacing: '0.02em',
  color: 'rgba(241,227,199,0.45)',
  marginTop: 2
};
const embeddedRowError: CSSProperties = {
  fontSize: '0.7rem',
  color: '#c0392b',
  marginTop: 2
};
const embeddedEditLink: CSSProperties = {
  // EDIT / CHANGE NUMBER — matches the design's computed style.
  background: 'transparent',
  border: 'none',
  color: '#ffb45c', // var(--orange-300)
  fontFamily: "'Oswald', sans-serif",
  fontSize: '13px',
  letterSpacing: '0.08em',
  fontWeight: 700,
  textTransform: 'uppercase',
  cursor: 'pointer',
  padding: 0,
  textShadow: '0 0 5px rgba(232, 130, 31, 0.25)',
  transition: 'color 0.15s ease'
};
const embeddedRowActions: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 4,
  flexShrink: 0
};
const embeddedVerifiedStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: '#4CAF50',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '13px',
  letterSpacing: '0.08em',
  fontWeight: 700,
  textTransform: 'uppercase'
};
// Unverified field (e.g. phone not verified on Descope) — amber warning, same
// Oswald 700 / 13px treatment as VERIFIED / EDIT.
const embeddedUnverifiedStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: '#e0a106',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '13px',
  letterSpacing: '0.08em',
  fontWeight: 700,
  textTransform: 'uppercase'
};
const embeddedEditWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8
};
const embeddedInputStyle: CSSProperties = {
  flex: 1,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(232,130,31,0.4)',
  borderRadius: 6,
  padding: '8px 10px',
  color: '#fff',
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none'
};
const embeddedSelectStyle: CSSProperties = {
  width: '100%',
  marginTop: 6,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(232,130,31,0.4)',
  borderRadius: 6,
  padding: '8px 10px',
  color: '#fff',
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  appearance: 'none',
  cursor: 'pointer'
};
const embeddedSaveBtn: CSSProperties = {
  background: '#e8821f',
  border: 'none',
  color: '#1a0e06',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  padding: '8px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease'
};
const avatarWrap: CSSProperties = {
  position: 'relative',
  boxSizing: 'border-box',
  width: HEADER_AVATAR_SIZE,
  height: HEADER_AVATAR_SIZE,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '2px solid rgba(255,255,255,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden'
};
const avatarImg: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%'
};
const avatarEditBtn: CSSProperties = {
  position: 'absolute',
  right: -2,
  bottom: -2,
  width: 22,
  height: 22,
  borderRadius: '999px',
  border: '1px solid rgba(233,140,38,0.35)',
  background: 'rgba(12,18,28,0.96)',
  color: '#E98C26',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 4px 10px rgba(0,0,0,0.28)'
};
const headerInfo: CSSProperties = { display: 'flex', flexDirection: 'column' };
const displayNameStyle: CSSProperties = { fontSize: '1.1rem', fontWeight: 700, color: '#fff' };
const guidStyle: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.3)',
  fontFamily: 'monospace'
};
const closeBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '10px',
  color: 'rgba(255,255,255,0.5)',
  padding: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};

const tabsRow: CSSProperties = {
  display: 'flex',
  gap: '2px',
  padding: '0 20px',
  marginTop: '8px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  flexShrink: 0
};
const tabBtn: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '10px 0',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const tabActive: CSSProperties = { color: '#E98C26', borderBottom: '2px solid #E98C26' };

const contentStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  scrollbarWidth: 'none',
  padding: '16px 20px'
};
const flashMessageStyle: CSSProperties = {
  padding: '10px 12px',
  borderRadius: '10px',
  fontSize: '0.78rem',
  fontWeight: 700,
  lineHeight: 1.35,
  marginBottom: '12px'
};
const flashSuccessStyle: CSSProperties = {
  color: '#7fe29b',
  background: 'rgba(76,175,80,0.12)',
  border: '1px solid rgba(76,175,80,0.28)'
};
const flashErrorStyle: CSSProperties = {
  color: '#ff8a8a',
  background: 'rgba(200,40,40,0.14)',
  border: '1px solid rgba(200,40,40,0.32)'
};
const phoneVerificationPanel: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '-2px',
  padding: '10px',
  borderRadius: '8px',
  background: 'rgba(99,179,237,0.08)',
  border: '1px solid rgba(99,179,237,0.2)'
};
const phoneVerificationHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  minHeight: '22px'
};
const phoneVerificationCloseBtn: CSSProperties = {
  marginLeft: 'auto',
  width: 24,
  height: 24,
  border: 'none',
  borderRadius: '6px',
  background: 'rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.65)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};
const phoneVerificationTitle: CSSProperties = {
  color: '#fff',
  fontSize: '0.75rem',
  fontWeight: 800
};
const phoneVerificationText: CSSProperties = {
  color: 'rgba(255,255,255,0.68)',
  fontSize: '0.72rem',
  lineHeight: 1.35
};
const phoneCodeRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 80px',
  gap: '8px',
  alignItems: 'center'
};
const phoneCodeRowCompact: CSSProperties = {
  gridTemplateColumns: '1fr'
};
const phoneCodeBoxesWrap: CSSProperties = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  height: '42px',
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
  gap: '6px',
  cursor: 'text'
};
const phoneCodeHiddenInput: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  border: 0,
  padding: 0,
  margin: 0,
  outline: 'none',
  cursor: 'text'
};
const phoneCodeBox: CSSProperties = {
  height: '42px',
  minWidth: 0,
  boxSizing: 'border-box',
  borderRadius: '6px',
  border: '1px solid rgba(99,179,237,0.42)',
  background: 'rgba(9,17,27,0.92)',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  letterSpacing: '0',
  fontFamily: 'inherit'
};
const phoneCodeBoxActive: CSSProperties = {
  border: '1px solid rgba(233,140,38,0.85)',
  boxShadow: '0 0 0 1px rgba(233,140,38,0.25)'
};
const phoneCodeBoxFilled: CSSProperties = {
  background: 'rgba(255,255,255,0.09)',
  border: '1px solid rgba(99,179,237,0.58)'
};
const phoneVerificationError: CSSProperties = {
  color: '#ff8a8a',
  fontSize: '0.72rem',
  fontWeight: 700
};
const phoneVerificationNotice: CSSProperties = {
  color: '#8bd69f',
  fontSize: '0.72rem',
  fontWeight: 700
};
const phoneResendRow: CSSProperties = {
  minHeight: '20px',
  display: 'flex',
  alignItems: 'center'
};
const phoneResendButton: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: '#63b3ed',
  padding: 0,
  fontSize: '0.72rem',
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
// Compact checkbox-style verify chip — mirrors the green "Verified" badge aesthetic.
const phoneVerifyChipBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  width: '100%',
  height: '42px',
  borderRadius: '8px',
  border: '1px solid',
  fontSize: '0.72rem',
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: 'transparent',
  transition: 'all 0.15s ease'
};
// 6 digits entered → green, matches the "Verified" badge palette
const phoneVerifyChipBtnReady: CSSProperties = {
  borderColor: 'rgba(76,175,80,0.55)',
  color: '#4CAF50',
  background: 'rgba(76,175,80,0.10)'
};
// Fewer than 6 digits → muted, clearly not actionable
const phoneVerifyChipBtnDim: CSSProperties = {
  borderColor: 'rgba(255,255,255,0.12)',
  color: 'rgba(255,255,255,0.28)',
  cursor: 'not-allowed'
};
const sectionCol: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '10px' };
const sectionTitle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};
const divider: CSSProperties = {
  height: 1,
  backgroundColor: 'rgba(255,255,255,0.06)',
  margin: '8px 0'
};
const twoCol: CSSProperties = { display: 'flex', gap: '10px' };

/* Field rows */
const fieldRow: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 };
const fieldRowHighlighted: CSSProperties = {
  background: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.35)',
  borderRadius: '8px',
  padding: '8px'
};
const fieldLabel: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.4)'
};
const fieldValueRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '36px'
};
const fieldValue: CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: '#fff', flex: 1 };
const fieldPlaceholder: CSSProperties = { ...fieldValue, color: 'rgba(255,255,255,0.25)' };
const fieldInput: CSSProperties = {
  flex: 1,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(233,140,38,0.3)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: 600,
  padding: '6px 10px',
  outline: 'none',
  fontFamily: 'inherit'
};
const fieldSelect: CSSProperties = { ...fieldInput, colorScheme: 'dark' };
const fieldInputInHighlightedRow: CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  borderColor: 'rgba(255,255,255,0.12)'
};
const editBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '6px',
  color: 'rgba(255,255,255,0.4)',
  padding: '4px 6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
const saveBtn: CSSProperties = {
  background: 'rgba(76,175,80,0.15)',
  border: 'none',
  borderRadius: '6px',
  color: '#4CAF50',
  padding: '4px 6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
const disabledSaveBtn: CSSProperties = { opacity: 0.75, cursor: 'wait' };
const savedText: CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: '#4CAF50'
};
const fieldErrorText: CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: '#ff8a8a'
};
const verifiedBadge: CSSProperties = {
  fontSize: '0.55rem',
  fontWeight: 700,
  color: '#4CAF50',
  backgroundColor: 'rgba(76,175,80,0.15)',
  padding: '2px 6px',
  borderRadius: '4px'
};
const inlineError: CSSProperties = {
  padding: '9px 10px',
  borderRadius: 8,
  border: '1px solid rgba(192,57,43,0.45)',
  background: 'rgba(192,57,43,0.15)',
  color: '#ffb4a9',
  fontSize: '0.72rem',
  fontWeight: 700,
  lineHeight: 1.35
};
const inlineSuccess: CSSProperties = {
  padding: '9px 10px',
  borderRadius: 8,
  border: '1px solid rgba(76,175,80,0.45)',
  background: 'rgba(76,175,80,0.14)',
  color: '#b9f6bf',
  fontSize: '0.72rem',
  fontWeight: 700,
  lineHeight: 1.35
};
const countryOptionStyle: CSSProperties = {
  backgroundColor: '#f7f1e8',
  color: '#111820',
  fontWeight: 600
};
const fieldMessage: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1.35,
  padding: '6px 8px',
  borderRadius: '8px',
  marginTop: '2px'
};
const fieldSuccessMessage: CSSProperties = {
  color: '#7fe29b',
  background: 'rgba(76,175,80,0.12)',
  border: '1px solid rgba(76,175,80,0.28)'
};
const fieldNeutralMessage: CSSProperties = {
  color: 'rgba(255,255,255,0.62)',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)'
};
const fieldErrorMessage: CSSProperties = {
  color: '#ff8a8a',
  background: 'rgba(200,40,40,0.14)',
  border: '1px solid rgba(200,40,40,0.32)'
};
const suggestionsWrap: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '4px'
};
const suggestionBtn: CSSProperties = {
  background: 'rgba(233,140,38,0.14)',
  border: '1px solid rgba(233,140,38,0.32)',
  borderRadius: '8px',
  color: '#f4b56b',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.7rem',
  fontWeight: 700,
  padding: '5px 8px'
};

/* Action cards */
const actionCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '14px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const actionCardInfo: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left'
};
const actionCardLabel: CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: '#fff' };
const actionCardDesc: CSSProperties = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' };

const biometricTogglePill: CSSProperties = {
  flexShrink: 0,
  padding: '3px 8px',
  borderRadius: '999px',
  fontSize: '0.62rem',
  fontWeight: 800,
  letterSpacing: '0.04em'
};
const biometricTogglePillOn: CSSProperties = {
  background: 'rgba(76,175,80,0.18)',
  color: '#4CAF50',
  border: '1px solid rgba(76,175,80,0.35)'
};
const biometricTogglePillOff: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.3)',
  border: '1px solid rgba(255,255,255,0.1)'
};

/* Verification */
const verificationCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  padding: '24px 16px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.06)',
  textAlign: 'center'
};
const verStatusLabel: CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#fff' };
const verStatusBadge: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  padding: '3px 10px',
  borderRadius: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};
const verStatusDesc: CSSProperties = {
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.4
};
const verifyBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 20px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '4px'
};

/* Profile details */
const profileDetailsCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '16px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.06)'
};
const feeRow: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' };
const feeValue: CSSProperties = { fontSize: '0.95rem', fontWeight: 700, color: '#fff' };
const feeCurrency: CSSProperties = { color: '#E98C26', marginRight: 2 };

const logoutBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '100%',
  padding: '12px',
  backgroundColor: 'rgba(200, 40, 40, 0.15)',
  border: '1px solid rgba(200, 40, 40, 0.3)',
  borderRadius: '12px',
  color: '#c0392b',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '8px'
};

const versionFooter: CSSProperties = {
  // justifyContent, not textAlign: VersionLabel lays its two parts out as a
  // flex row, and textAlign does not move flex items.
  justifyContent: 'center',
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.35)',
  marginTop: '12px',
  letterSpacing: '0.02em'
};

/* ── Table Settings sub-view styles ── */

const subHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '20px 20px 16px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  flexShrink: 0
};
const subBackBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '10px',
  color: 'rgba(255,255,255,0.7)',
  padding: '8px 10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
const subHeaderTitle: CSSProperties = {
  flex: 1,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff',
  textAlign: 'center'
};

const togglePillStyle: CSSProperties = {
  width: 36,
  height: 20,
  borderRadius: 999,
  padding: 2,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  transition: 'background-color 0.15s ease'
};
const toggleKnobStyle: CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  transition: 'transform 0.15s ease'
};

/* Theme picker */
const themeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '14px'
};
const themeCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '14px 10px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  border: '2px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};
const themePreviewStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '0.85 / 1',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const themePreviewOvalStyle: CSSProperties = {
  width: '70%',
  height: '70%',
  borderRadius: '999px'
};
const themeNameStyle: CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#fff'
};
const themeSaveBarStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '14px 20px',
  // This bar is absolutely positioned, so it overlaps the panel's bottom
  // safe-area padding instead of being pushed up by it. Add the inset to its
  // own bottom padding so the button clears the home indicator while the bar
  // background still fills to the screen edge.
  paddingBottom: `calc(14px + ${SAFE_AREA_BOTTOM})`,
  borderTop: '1px solid rgba(255,255,255,0.06)',
  backgroundColor: 'rgba(12,18,28,0.98)',
  flexShrink: 0
};
const themeSaveBtn: CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'linear-gradient(135deg, #E98C26, #d47a1a)',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
